import { getKioskToken } from './kiosk-token';
import type {
  Account,
  AccountCredit,
  ApproveRefundRequestArgs,
  BillingStatus,
  BookingSearchRow,
  BrandJson,
  Building,
  CancellationPoliciesResponse,
  CancellationPolicy,
  CommDelivery,
  CommDeliveryStatus,
  CommFailure,
  CommFailureClass,
  CreateBuildingInput,
  CreateDiscountCodeInput,
  CreateGroomerInput,
  CreateIncidentInput,
  CreateMembershipTierInput,
  CreatePetBelongingInput,
  CreatePosProductInput,
  CreatePricingRuleInput,
  CreateRunInput,
  CreateServiceInput,
  CreateTransportInput,
  CreateUnavailabilityInput,
  CreateVehicleInput,
  CreateVetInput,
  CustomHostnameState,
  Customer,
  CustomerBookingRow,
  DiscountCode,
  EnrichedRefundRequest,
  Groomer,
  Incident,
  IssueCreditInput,
  KioskCurrentResponse,
  KioskListItem,
  ListIncidentsFilters,
  MeResponse,
  MembershipTier,
  NotificationTemplate,
  NotifyOwnerIncidentInput,
  Pet,
  PetBelonging,
  PetVetLink,
  PetWithVaccinations,
  PosPayment,
  PosProduct,
  PosSession,
  PosStockMovement,
  PosTransaction,
  PosTransactionLine,
  PricingRule,
  Quote,
  QuoteInput,
  RefundRequest,
  RefundStatus,
  Role,
  Run,
  RunUnavailabilityWindow,
  SaveTemplateInput,
  SendingDomainState,
  ServiceCatalogueItem,
  ServiceTaskRow,
  SignupTenantInput,
  SignupTenantResult,
  SmsProviderState,
  SmsProviderUpsertInput,
  TeamMember,
  TemplateVersionSummary,
  TransportAssignment,
  UpdateAccountInput,
  UpdateBuildingInput,
  UpdateCustomerInput,
  UpdateDiscountCodeInput,
  UpdateGroomerInput,
  UpdateIncidentInput,
  UpdateMembershipInput,
  UpdateMembershipTierInput,
  UpdatePetBelongingInput,
  UpdatePosProductInput,
  UpdatePricingRuleInput,
  UpdateRunInput,
  UpdateServiceInput,
  UpdateVehicleInput,
  UpdateVetInput,
  UpsertCancellationPolicyInput,
  Vehicle,
  Vet,
  WaitlistEntry,
  WaitlistStatus,
} from './types';

const API = '/api';

interface RequestOpts {
  kiosk?: boolean;
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
}

async function request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
  const url = new URL(API + path, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.kiosk) {
    const token = getKioskToken();
    if (!token) throw new ApiError('no_kiosk_token', 0);
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(url.toString(), {
    method,
    credentials: 'include',
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const code = typeof body.error === 'string' ? body.error : `http_${res.status}`;
    throw new ApiError(code, res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    // biome-ignore lint/suspicious/noExplicitAny: error body shape is untyped by design
    public readonly body: any = null,
  ) {
    super(`${status} ${code}`);
  }
}

// ----- Waiver sign-by-link (staff mint)
export interface WaiverLinkResult {
  url: string;
  expiresAt: string;
  waiverVersion: number;
  sent: boolean;
  channels: string[];
}

export interface CreateWaiverLinkInput {
  send: boolean;
  channels?: Array<'email' | 'sms'>;
}

/**
 * Mint a 7-day no-login waiver signing link for a customer. With
 * `send: true` the server also delivers it via the `customer.waiver_link`
 * trigger on the requested channels. Capability: customers.edit.
 */
export async function createCustomerWaiverLink(
  customerId: string,
  input: CreateWaiverLinkInput,
): Promise<WaiverLinkResult> {
  return request<WaiverLinkResult>('POST', `/customers/${customerId}/waiver-link`, {
    body: input,
  });
}

// ----- Signup (public — no session cookie required)
export async function signupTenant(input: SignupTenantInput): Promise<SignupTenantResult> {
  const {
    accountName,
    currency,
    defaultTimezone,
    locale,
    ownerEmail,
    ownerPassword,
    ownerName,
    firstFacilityName,
  } = input;
  const body = {
    accountName,
    currency,
    defaultTimezone,
    locale,
    ownerEmail,
    ownerPassword,
    ownerName,
    firstFacilityName,
    ...(input.accountSlug ? { accountSlug: input.accountSlug } : {}),
    ...(input.firstFacilitySlug ? { firstFacilitySlug: input.firstFacilitySlug } : {}),
  };
  return request<SignupTenantResult>('POST', '/signup/tenant', { body });
}

// ----- Auth-adjacent
export const getMe = () => request<MeResponse>('GET', '/me');
export const setActiveAccount = (accountId: string) =>
  request<{ ok: true; activeAccountId: string }>('POST', '/me/active-account', {
    body: { accountId },
  });
export const setActiveFacility = (facilityId: string | null) =>
  request<{ ok: true; activeFacilityId: string | null }>('POST', '/me/active-facility', {
    body: { facilityId },
  });

export const updateAccount = (patch: UpdateAccountInput) =>
  request<{ account: Account }>('PATCH', '/me/account', { body: patch });

// ----- Customers + pets
export const searchCustomers = (q?: string) =>
  request<Customer[]>('GET', '/customers', { query: { q } });
export const getCustomer = (id: string) =>
  request<Customer & { pets: Pet[] }>('GET', `/customers/${id}`);
export const createCustomer = (body: import('./types').CreateCustomerInput) =>
  request<Customer>('POST', '/customers', { body });
export const updateCustomer = (id: string, patch: UpdateCustomerInput) =>
  request<Customer>('PATCH', `/customers/${id}`, { body: patch });
// Sub-spec 02 Plan 4 M5 — issue an account credit on the staff CustomerDetail
// page. 403 when caller lacks customers.credit_issue (manager + owner only).
export const issueCustomerCredit = (
  customerId: string,
  body: IssueCreditInput,
): Promise<{ credit: AccountCredit }> =>
  request<{ credit: AccountCredit }>('POST', `/customers/${customerId}/credits`, { body });

export const listCustomerCredits = (customerId: string): Promise<{ credits: AccountCredit[] }> =>
  request<{ credits: AccountCredit[] }>('GET', `/customers/${customerId}/credits`);

export const voidCustomerCredit = (
  customerId: string,
  creditId: string,
): Promise<{ voided: boolean }> =>
  request<{ voided: boolean }>('DELETE', `/customers/${customerId}/credits/${creditId}`);

export const getCustomerBookings = (id: string) =>
  request<CustomerBookingRow[]>('GET', `/customers/${id}/bookings`);
export const listPetsByCustomer = (customerId: string) =>
  request<PetWithVaccinations[]>('GET', `/pets/by-customer/${customerId}`);
export const createPet = (body: {
  customerId: string;
  name: string;
  species: 'dog' | 'cat';
  breed?: string | null | undefined;
  sizeCategory?: string | null | undefined;
  dateOfBirth?: string | null | undefined;
  sex?: string | null | undefined;
  neutered?: number | null | undefined;
  color?: string | null | undefined;
  microchip?: string | null | undefined;
  notes?: string | null | undefined;
  weightKg?: number | null | undefined;
  feedingInstructions?: string | null | undefined;
  allergiesJson?: string | null | undefined;
  medicationsJson?: string | null | undefined;
  medicalConditions?: string | null | undefined;
  behaviorNotes?: string | null | undefined;
  specialInstructions?: string | null | undefined;
  insuranceProvider?: string | null | undefined;
  insurancePolicyNumber?: string | null | undefined;
  insuranceExpiresOn?: string | null | undefined;
  microchipRegisteredWith?: string | null | undefined;
  microchipRegistrationDate?: string | null | undefined;
}) => request<Pet>('POST', '/pets', { body });

export const updatePet = (id: string, patch: import('./types').UpdatePetInput) =>
  request<Pet>('PATCH', `/pets/${id}`, { body: patch });

// ----- Pet weight logs (migration 0068)
export interface PetWeightLogEntry {
  id: string;
  petId: string;
  weightGrams: number;
  weighedAt: string;
  note: string | null;
  recordedByMembershipId: string | null;
  createdAt: string;
}
export const listPetWeightLogs = (petId: string) =>
  request<{ items: PetWeightLogEntry[] }>('GET', `/pets/${petId}/weight-logs`);
export const recordPetWeight = (
  petId: string,
  input: { weightGrams: number; note?: string | null; weighedAt?: string },
) => request<{ entry: PetWeightLogEntry }>('POST', `/pets/${petId}/weight-logs`, { body: input });

// ----- Bookings
export interface CreateGroupBody {
  customerId: string;
  facilityId: string;
  checkIn: string;
  checkout: string;
  pets: Array<{
    petId: string;
    runId: string | null;
    feeding?: string | null | undefined;
    medication?: string | null | undefined;
    totalMinor?: number | null | undefined;
    serviceLineItems?:
      | Array<{
          serviceId: string;
          scheduledDate: string | null;
          priceMinor: number;
        }>
      | undefined;
  }>;
  // Transport legs (one per direction; 'both' expands server-side). The fee
  // is charged once via the group's primary booking total. Omit `feeMinor`
  // on every leg to let the server price from the account's transport
  // settings (using `distanceMiles` when supplied — see quoteTransport).
  transport?: Array<{
    direction: 'pickup' | 'dropoff' | 'both';
    vehicleId?: string | null;
    scheduledAt?: string | null;
    pickupAddress?: string | null;
    pickupWhat3words?: string | null;
    pickupLat?: number | null;
    pickupLng?: number | null;
    distanceMiles?: number | null;
    feeMinor?: number | null;
    notes?: string | null;
  }> | null;
  /** Bypass a `min_stay_not_met` 422 (needs pricing.rules.write; audit-logged). */
  overrideMinStay?: boolean;
  /** Bypass an `assessment_required` 422 (needs assessments.record; audit-logged). */
  overrideAssessment?: boolean;
}

/** One failing requirement from a 422 `vaccines_required` response. */
export interface VaccineGateMissing {
  vaccineId: string;
  vaccineName: string;
  expiresOn?: string;
  reason?: 'not_recorded' | 'expired' | 'expiring_before_visit';
}

export const createBookingGroup = (body: CreateGroupBody) =>
  request<{ groupId: string; bookings: Array<{ id: string; bookingGroupId: string }> }>(
    'POST',
    '/bookings/group',
    { body },
  );
// ---------------------------------------------------------------------------
// Booking group detail — enriched shape returned by GET /api/bookings/group/:id
// ---------------------------------------------------------------------------

export interface BookingGroupVaccination {
  id: string;
  vaccineId: string;
  vaccineName: string;
  expiresOn: string | null;
  valid: boolean;
}

export interface BookingGroupPet {
  id: string;
  name: string;
  species: string;
  breed: string | null;
  photoFileId: string | null;
  vaccinations: BookingGroupVaccination[];
}

export interface BookingGroupStay {
  bookingId: string;
  runId: string | null;
  runName: string | null;
  checkIn: string;
  checkout: string;
}

export interface BookingGroupLineItem {
  id: string;
  serviceId: string;
  serviceName: string | null;
  priceMinor: number;
  /** Migration 0103 — 'service' (add-on) or 'overstay_night' (one per extra
   * night; `scheduledDate` carries the facility-local night). */
  kind?: 'service' | 'overstay_night';
  scheduledDate?: string | null;
}

export interface BookingGroupIntakeResponse {
  questionSlug: string;
  answer: boolean | number;
  note: string | null;
}

export interface BookingGroupEntry {
  booking: {
    id: string;
    type: string;
    status: string;
    petId: string;
    customerId: string;
    facilityId: string;
    bookingGroupId: string | null;
    createdAt: string;
    // Migration 0086 — staff notes + colour tag per booking.
    notes: string | null;
    colour: string | null;
  };
  stay: BookingGroupStay | null;
  // Grooming-only: the appointment's actual start time and duration.
  groomingStartAt: string | null;
  groomingDurationMin: number | null;
  lineItems: BookingGroupLineItem[];
  intakeResponses: BookingGroupIntakeResponse[];
  pet: BookingGroupPet;
}

export interface BookingGroupCustomer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
}

export interface BookingGroupDetail {
  groupId: string;
  customer: BookingGroupCustomer | null;
  bookings: BookingGroupEntry[];
}

export const getBookingGroup = (groupId: string) =>
  request<BookingGroupDetail>('GET', `/bookings/group/${groupId}`);

/** Fetch a single booking by its individual booking ID. Returns the raw Booking row including bookingGroupId. */
// biome-ignore lint/suspicious/noExplicitAny: full booking schema defined in api layer
export const getBooking = (bookingId: string) =>
  request<Record<string, any>>('GET', `/bookings/${bookingId}`);
/** Migration 0094 — SOFT delete; reversible via restoreBookingGroup for 30 days. */
export const deleteBookingGroup = (groupId: string) =>
  request<{ ok: true; deletedBookingIds: string[]; deletedAt: string; restorableUntil: string }>(
    'DELETE',
    `/bookings/group/${groupId}`,
  );
export const restoreBookingGroup = (groupId: string) =>
  request<{ ok: true; restoredBookingIds: string[]; runClearedBookingIds: string[] }>(
    'POST',
    `/bookings/group/${groupId}/restore`,
  );
export const restoreBooking = (bookingId: string) =>
  request<{ ok: true; runCleared: boolean }>('POST', `/bookings/${bookingId}/restore`);
export const sendGroupToKiosk = (groupId: string) =>
  request<{ ok: true }>('POST', `/bookings/group/${groupId}/send-to-kiosk`);
export const cancelBooking = (id: string, body?: { reason?: string; note?: string }) =>
  request<{ ok: true }>('POST', `/bookings/${id}/cancel`, { body });
export const staffCheckoutBooking = (id: string, body?: { actualCheckout?: string }) =>
  request<{ ok: true }>('POST', `/staff/bookings/${id}/checkout`, { body });

export const staffCheckinBooking = (id: string) =>
  request<{ ok: true }>('POST', `/staff/bookings/${id}/checkin`);

export const staffChargeBookingManual = (
  id: string,
  body: { amountMinor: number; source: 'cash' | 'check' | 'manual' },
) => request<{ ok: true; paymentId: string }>('POST', `/staff/bookings/${id}/charge`, { body });

/**
 * Trigger an off-session charge against the customer's saved card on file.
 * Returns `{ ok: true }` on success, or throws ApiError with `.code` =
 * 'no_default_pm' | 'connect_off' | 'stripe_off' | 'pi_failed' | etc.
 */
export const staffChargeBalance = (id: string) =>
  request<{ ok: true; payment_intent_id: string }>('POST', `/staff/bookings/${id}/charge-balance`, {
    body: {},
  });

/**
 * Create an on-session PaymentIntent for the outstanding booking balance so
 * staff can key in a card with Stripe.js (Payment Element).
 * Returns { client_secret, stripe_account_id, amount_minor, currency }.
 */
export const staffGetBookingPaymentIntent = (id: string) =>
  request<{
    client_secret: string;
    stripe_account_id: string;
    amount_minor: number;
    currency: string;
  }>('POST', `/staff/bookings/${id}/payment-intent`, { body: {} });

export interface EarlyCheckoutPreview {
  adjustedQuoteMinor: number;
  currency: string;
  nightsSaved: number;
  originalNights: number;
  actualNights: number;
  /** Migration 0103 — nights the adjusted total actually covers once
   * cron-charged overstay nights are honoured (>= actualNights). */
  chargedNights?: number;
  overstay?: {
    nights: number;
    nightDates: string[];
    feesMinor: number;
    mode: 'nightly_rate' | 'flat_fee' | 'nightly_rate_plus_fee' | null;
  };
}

export const getEarlyCheckoutPreview = (
  bookingId: string,
  actualCheckout: string,
): Promise<EarlyCheckoutPreview> =>
  request<EarlyCheckoutPreview>('POST', `/bookings/${bookingId}/early-checkout-preview`, {
    body: { actualCheckout },
  });

// --- Booking detail enrichment (migration 0086) --------------------------

export interface BookingPayment {
  id: string;
  flow: string;
  kind: string;
  source: string;
  direction: 'in' | 'out';
  amountMinor: number;
  currency: string;
  status: string;
  externalId: string | null;
  createdAt: string;
  metadata: string | null;
}

export interface BookingCommRow {
  id: string;
  triggerKey: string;
  channel: string;
  status: string;
  enqueuedAt: string;
  sentAt: string | null;
  lastError: string | null;
}

export const patchBookingMetadata = (
  id: string,
  patch: { notes?: string | null; colour?: string | null },
) =>
  request<{ booking: Record<string, unknown> }>('PATCH', `/bookings/${id}/metadata`, {
    body: patch,
  });

export const patchBookingStayDates = (
  id: string,
  patch: {
    checkIn: string;
    checkout: string;
    feeding?: string | null;
    medication?: string | null;
  },
) => request<{ ok: true }>('PATCH', `/bookings/${id}/stay-dates`, { body: patch });

export const listBookingPayments = (id: string) =>
  request<{ payments: BookingPayment[] }>('GET', `/bookings/${id}/payments`);

export const listBookingComms = (id: string) =>
  request<{ comms: BookingCommRow[] }>('GET', `/bookings/${id}/comms`);

export const searchBookings = (filters: {
  q?: string | undefined;
  species?: string | undefined;
  service?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  checkInDate?: string | undefined;
  checkoutDate?: string | undefined;
  facilityId?: string | undefined;
  status?: string | undefined;
}) => request<BookingSearchRow[]>('GET', '/bookings/search', { query: filters });

// ----- World-class Calendar (day/week swimlanes + drag-reschedule)
export type CalendarView = 'day' | 'week';
export interface CalendarResource {
  id: string;
  type: 'run' | 'groomer';
  name: string;
  facilityId: string | null;
  capacityMax: number;
}
export interface CalendarBookingBar {
  bookingId: string;
  bookingGroupId: string | null;
  bookingType: 'boarding' | 'daycare' | 'grooming' | 'service';
  status: string;
  category: 'boarding' | 'daycare' | 'grooming' | 'service';
  resourceId: string | null;
  petId: string;
  petName: string;
  customerId: string;
  customerName: string;
  startIso: string;
  endIso: string;
  /** True when a checked_in boarding stay is past its scheduled checkout (overstay). */
  isOverstay?: boolean;
}
export interface CalendarOccupancyCell {
  date: string;
  capacity: number;
  used: number;
  pct: number;
}
export interface CalendarSnapshot {
  facilityId: string;
  timezone: string;
  view: CalendarView;
  from: string;
  to: string;
  resources: CalendarResource[];
  bookings: CalendarBookingBar[];
  occupancy: CalendarOccupancyCell[];
}
export interface CalendarConflict {
  kind: 'capacity' | 'overlap' | 'inactive_resource' | 'facility_mismatch' | 'invalid_input';
  message: string;
  bookingId?: string;
}
export interface NextAvailableResult {
  found: boolean;
  resourceId?: string;
  resourceName?: string;
  startIso?: string;
  endIso?: string;
  groomerId?: string;
  groomerName?: string;
  date?: string;
  startTime?: string;
}

export const fetchCalendarSnapshot = (opts: {
  facilityId: string;
  from: string;
  to: string;
  view: CalendarView;
}) =>
  request<CalendarSnapshot>('GET', '/calendar/snapshot', {
    query: { facility: opts.facilityId, from: opts.from, to: opts.to, view: opts.view },
  });

export const checkCalendarConflicts = (body: {
  bookingId?: string;
  bookingType: 'boarding' | 'grooming';
  facilityId: string;
  startIso: string;
  endIso?: string;
  durationMin?: number;
  runId?: string | null;
  groomerId?: string | null;
}) => request<{ conflicts: CalendarConflict[] }>('POST', '/calendar/check-conflicts', { body });

export const fetchNextAvailable = (opts: {
  facilityId: string;
  category: 'boarding' | 'daycare' | 'grooming';
  from: string;
  duration?: number;
}) =>
  request<NextAvailableResult>('GET', '/calendar/next-available', {
    query: {
      facility: opts.facilityId,
      category: opts.category,
      from: opts.from,
      ...(opts.duration !== undefined ? { duration: opts.duration } : {}),
    },
  });

export const moveBookingDates = (
  bookingId: string,
  body:
    | { newCheckIn: string; newCheckout: string }
    | { newStartAt: string; newDurationMin?: number },
) =>
  request<{ ok: true } & Record<string, unknown>>(
    'PATCH',
    `/calendar/bookings/${bookingId}/move-dates`,
    { body },
  );

// ----- World-class /today dashboard
export interface TodaySnapshotOccupant {
  bookingId: string;
  petId: string;
  petName: string;
  petSpecies: string;
  petPhotoFileId: string | null;
  customerId: string;
  customerName: string;
  customerPhone: string | null;
  checkout: string;
  status: string;
}
export interface TodaySnapshotRun {
  id: string;
  buildingId: string | null;
  name: string;
  sizeCategory: string;
  capacityMax: number;
  /** Species this run takes (`runs.species_allowed`), e.g. `['dog']`. */
  speciesAllowed: string[];
  /** All pets currently assigned to this run. Empty array = vacant. */
  occupants: TodaySnapshotOccupant[];
}
export interface TodaySnapshotBuilding {
  id: string;
  name: string;
  position: number;
}
export interface TodaySnapshotEvent {
  type: 'checkin' | 'checkout' | 'grooming' | 'daycare';
  bookingId: string;
  petName: string;
  customerName: string;
  timeIso: string;
  note: string | null;
}
export interface TodaySnapshotSuggestion {
  id: string;
  severity: 'info' | 'warn' | 'critical';
  title: string;
  body: string;
  actionLabel: string;
  actionHref: string;
}
export interface TodaySnapshot {
  facilityId: string;
  timezone: string;
  today: string;
  lastWeekDate: string;
  metrics: {
    occupancy: {
      occupiedCount: number;
      totalCapacity: number;
      occupancyPct: number;
      lastWeekCount: number;
      delta: number;
    };
    revenue: { totalMinor: number; currency: string; paymentCount: number };
    petsInCare: { total: number; boarding: number; daycare: number; grooming: number };
  };
  floorPlan: {
    buildings: TodaySnapshotBuilding[];
    runs: TodaySnapshotRun[];
    unassignedStays: TodaySnapshotOccupant[];
  };
  events: TodaySnapshotEvent[];
  suggestions: TodaySnapshotSuggestion[];
  aiBrief: string | null;
}
export const fetchTodaySnapshot = (facilityId: string) =>
  request<TodaySnapshot>('GET', '/today/snapshot', { query: { facilityId } });

export const moveBoardingStayRun = (bookingId: string, runId: string | null) =>
  request<{ ok: true; runId: string | null; previousRunId: string | null }>(
    'PATCH',
    `/boarding-stays/${bookingId}/move-run`,
    { body: { runId } },
  );

export interface CopilotAnomaly {
  kind: string;
  severity: 'info' | 'warn' | 'critical';
  message: string;
  // biome-ignore lint/suspicious/noExplicitAny: anomaly payload shape is feature-evolving
  metadata?: any;
}
export const fetchCopilotAnomalies = (date?: string) =>
  request<{ date: string; anomalies: CopilotAnomaly[]; cached: boolean; model?: string }>(
    'GET',
    '/copilot/anomalies',
    { query: { date } },
  );

// Sub-spec 05 Plan 4 — grooming-during-boarding sub-booking helpers.
export interface PendingGroomingQueueEntry {
  booking: {
    id: string;
    status: string;
    customerId: string;
    petId: string;
    parentBookingId: string | null;
    type: string;
  };
  appointment: {
    bookingId: string;
    groomerId: string | null;
    startAt: string;
    durationMin: number;
    serviceIds: string;
  };
  parent: {
    id: string;
    type: string;
    petId: string;
    customerId: string;
  };
}
export const listPendingGroomingQueue = (filters: {
  facilityId?: string | undefined;
  date?: string | undefined;
}) =>
  request<{ entries: PendingGroomingQueueEntry[] }>('GET', '/bookings/pending-grooming-queue', {
    query: filters,
  });

export interface AssignGroomerInput {
  groomerId: string;
  date: string; // YYYY-MM-DD
  startAtLocal: string; // HH:MM
  durationMin?: number;
}
export const assignGroomerToSubBooking = (bookingId: string, body: AssignGroomerInput) =>
  request<{
    bookingId: string;
    status: string;
    groomerId: string;
    startAt: string;
    totalMinor: number;
  }>('POST', `/bookings/${bookingId}/assign-groomer`, { body });

export const exportBookingsCsvUrl = (filters: Record<string, string | undefined>): string => {
  const url = new URL(`${API}/bookings/search/export`, window.location.origin);
  for (const [k, v] of Object.entries(filters)) {
    if (v) url.searchParams.set(k, v);
  }
  return url.toString();
};

// ----- Runs
export const getRuns = (facilityId: string, includeInactive = false) =>
  request<Run[]>('GET', '/runs', {
    query: { facilityId, ...(includeInactive ? { includeInactive: 'true' } : {}) },
  });
export const createRun = (body: CreateRunInput): Promise<Run> =>
  request<Run>('POST', '/runs', { body });
export const updateRun = (id: string, patch: UpdateRunInput): Promise<Run> =>
  request<Run>('PATCH', `/runs/${id}`, { body: patch });
export const deleteRun = (id: string): Promise<void> => request<void>('DELETE', `/runs/${id}`);

// ----- Run unavailability windows (migration 0052)
export const getRunUnavailability = (runId: string): Promise<RunUnavailabilityWindow[]> =>
  request<RunUnavailabilityWindow[]>('GET', `/runs/${runId}/unavailability`);
export const createRunUnavailability = (
  runId: string,
  body: CreateUnavailabilityInput,
): Promise<RunUnavailabilityWindow> =>
  request<RunUnavailabilityWindow>('POST', `/runs/${runId}/unavailability`, { body });
export const deleteRunUnavailability = (runId: string, windowId: string): Promise<void> =>
  request<void>('DELETE', `/runs/${runId}/unavailability/${windowId}`);

// ----- Buildings (migration 0052)
export const getBuildings = (facilityId: string, includeInactive = false): Promise<Building[]> =>
  request<Building[]>('GET', '/buildings', {
    query: { facilityId, ...(includeInactive ? { includeInactive: 'true' } : {}) },
  });
export const createBuilding = (body: CreateBuildingInput): Promise<Building> =>
  request<Building>('POST', '/buildings', { body });
export const updateBuilding = (id: string, patch: UpdateBuildingInput): Promise<Building> =>
  request<Building>('PATCH', `/buildings/${id}`, { body: patch });
export const deleteBuilding = (id: string): Promise<void> =>
  request<void>('DELETE', `/buildings/${id}`);

// ----- Groomers (sub-spec 05 Plan 1)
export const listGroomers = (includeInactive = false) =>
  request<{ groomers: Groomer[] }>('GET', '/groomers', {
    query: includeInactive ? { includeInactive: 'true' } : {},
  });
export const getGroomer = (id: string) => request<{ groomer: Groomer }>('GET', `/groomers/${id}`);
export const createGroomer = (body: CreateGroomerInput) =>
  request<{ groomer: Groomer }>('POST', '/groomers', { body });
export const updateGroomer = (id: string, patch: UpdateGroomerInput) =>
  request<{ groomer: Groomer }>('PATCH', `/groomers/${id}`, { body: patch });
export const deleteGroomer = (id: string) => request<{ ok: true }>('DELETE', `/groomers/${id}`);

// Groomer schedule + exceptions (sub-spec 05 Plan 1).
// GroomerScheduleResponse intentionally matches the API's `defaultSchedule`
// shape — `Record<'1'..'7', DaySchedule | null>`. The web side keeps it as
// `Record<string, ScheduleDay | null> | null` to avoid duplicating the type.
export interface ScheduleBreak {
  start: string;
  end: string;
}
export interface ScheduleDay {
  startTime: string;
  endTime: string;
  breaks: ScheduleBreak[];
}
export type GroomerDefaultSchedule = Record<string, ScheduleDay | null> | null;
export interface GroomerScheduleException {
  id: string;
  date: string;
  overrideType: 'unavailable' | 'shifted';
  startAt: string | null;
  endAt: string | null;
  note: string | null;
}
export interface GroomerScheduleResponse {
  defaultSchedule: GroomerDefaultSchedule;
  exceptions: GroomerScheduleException[];
}
export interface CreateGroomerScheduleExceptionBody {
  date: string;
  overrideType: 'unavailable' | 'shifted';
  startAt?: string;
  endAt?: string;
  note?: string | null;
}
export const getGroomerSchedule = (id: string) =>
  request<GroomerScheduleResponse>('GET', `/groomers/${id}/schedule`);
export const setGroomerSchedule = (id: string, defaultSchedule: GroomerDefaultSchedule) =>
  request<{ groomer: Groomer }>('PUT', `/groomers/${id}/schedule`, { body: { defaultSchedule } });
export const createGroomerScheduleException = (
  id: string,
  body: CreateGroomerScheduleExceptionBody,
) =>
  request<{ exception: GroomerScheduleException }>('POST', `/groomers/${id}/exceptions`, { body });
export const deleteGroomerScheduleException = (groomerId: string, exceptionId: string) =>
  request<{ ok: true }>('DELETE', `/groomers/${groomerId}/exceptions/${exceptionId}`);

// ----- Grooming appointments (sub-spec 05 Plan 2)
export interface GroomingAppointmentRow {
  booking: {
    id: string;
    customerId: string;
    petId: string;
    facilityId: string;
    status: string;
    isWalkIn: number;
  };
  appointment: {
    bookingId: string;
    groomerId: string | null;
    startAt: string;
    durationMin: number;
    serviceIds: string;
    status: string;
  };
}

export const listGroomingAppointments = (filters: {
  date?: string;
  groomerId?: string;
  facilityId?: string;
}) => request<GroomingAppointmentRow[]>('GET', '/grooming-appointments', { query: filters });

export const createGroomingAppointment = (body: {
  customerId: string;
  petId: string;
  facilityId: string;
  groomerId: string | null;
  startAt: string;
  durationMin?: number;
  serviceIds: string[];
  notes?: string | null;
}) => request<GroomingAppointmentRow>('POST', '/grooming-appointments', { body });

export const createWalkInGroomingAppointment = (body: {
  customerId: string;
  petId: string;
  groomerId: string;
  serviceIds: string[];
  facilityId?: string;
  durationMin?: number;
  notes?: string | null;
  forceOverride?: boolean;
  overrideReason?: string | null;
}) => request<GroomingAppointmentRow>('POST', '/grooming-appointments/walk-in', { body });

// ----- Grooming slots (sub-spec 05 Plan 1)
export interface GroomingSlot {
  startAt: string;
  durationMin: number;
}

export const listGroomingSlots = (filters: {
  facilityId: string;
  date: string;
  durationMin: number;
  serviceId: string;
  groomerId?: string;
}) =>
  request<{ slots: Record<string, GroomingSlot[]> }>('GET', '/grooming/slots', { query: filters });

// ----- Services
export const listServices = (facilityId?: string) =>
  request<ServiceCatalogueItem[]>('GET', '/services', { query: { facilityId } });
export const listServiceTasks = (date: string, facilityId: string) =>
  request<ServiceTaskRow[]>('GET', '/services/tasks', { query: { date, facilityId } });
export const setLineItemStatus = (id: string, status: 'pending' | 'done' | 'skipped') =>
  request<{ ok: true }>('PATCH', `/services/line-items/${id}`, { body: { status } });
export const createService = (body: CreateServiceInput): Promise<ServiceCatalogueItem> =>
  request<ServiceCatalogueItem>('POST', '/services', { body });
export const updateService = (
  id: string,
  patch: UpdateServiceInput,
): Promise<ServiceCatalogueItem> =>
  request<ServiceCatalogueItem>('PATCH', `/services/${id}`, { body: patch });
export const deleteService = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/services/${id}`);

// ----- Kiosks (admin)
export const listKiosks = () => request<KioskListItem[]>('GET', '/kiosks');
export const provisionKiosk = (body: { facilityId: string; name: string }) =>
  request<{ kiosk: KioskListItem; token: string }>('POST', '/kiosks', { body });
export const revokeKiosk = (id: string) => request<{ ok: true }>('DELETE', `/kiosks/${id}`);
export const deleteKioskPermanently = (id: string) =>
  request<{ ok: true }>('DELETE', `/kiosks/${id}/permanent`);
export const rotateKiosk = (id: string) =>
  request<{ token: string }>('POST', `/kiosks/${id}/rotate`);

// ----- Waitlist
export const listWaitlist = (
  filters: { facilityId?: string | undefined; status?: string | undefined } = {},
) => request<WaitlistEntry[]>('GET', '/waitlist', { query: filters });
export const createWaitlistEntry = (body: {
  facilityId: string;
  customerId: string;
  petId: string;
  requestedCheckIn: string;
  requestedCheckout: string;
  notes?: string | undefined;
}) => request<WaitlistEntry>('POST', '/waitlist', { body });
export const updateWaitlistEntry = (
  id: string,
  body: { status: WaitlistStatus; notes?: string | undefined },
) => request<WaitlistEntry>('PATCH', `/waitlist/${id}`, { body });
export const promoteWaitlistEntry = (
  id: string,
  body: {
    checkIn: string;
    checkout: string;
    runId?: string | null | undefined;
    feeding?: string | null | undefined;
    medication?: string | null | undefined;
  },
) =>
  request<{
    booking: { id: string; accountId: string };
    groupId: string;
    waitlistEntry: WaitlistEntry;
  }>('POST', `/waitlist/${id}/promote`, { body });
export const deleteWaitlistEntry = (id: string) =>
  request<{ ok: true }>('DELETE', `/waitlist/${id}`);

// ----- Kiosk-side (token-authenticated)
export const kioskConnect = () =>
  request<{
    accountId: string;
    facilityId: string;
    facilityName: string;
    kiosk: { id: string; name: string };
  }>('POST', '/kiosks/connect', { kiosk: true });
export const kioskCurrent = () =>
  request<KioskCurrentResponse | null>('GET', '/checkin/current', { kiosk: true });
export const kioskComplete = (
  bookingId: string,
  body: {
    feeding: string | null;
    medication: string | null;
    selectedServiceIds: string[];
    questions: Record<string, { answer: boolean | 0 | 1; note?: string | null | undefined }>;
  },
) => request<{ ok: true }>('POST', `/checkin/${bookingId}/complete`, { kiosk: true, body });

// ----- Team
export const getTeam = () => request<TeamMember[]>('GET', '/team');
export const getTeamRoles = () => request<Role[]>('GET', '/team/roles');
export const updateTeamMember = (id: string, patch: UpdateMembershipInput) =>
  request<TeamMember>('PATCH', `/team/${id}`, { body: patch });
export const removeTeamMember = (id: string) => request<{ ok: true }>('DELETE', `/team/${id}`);

// ----- Team invitations
export interface TeamInvitation {
  id: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expiresAt: string;
  createdAt: string;
  facilityScopeIds: string[] | null;
}

export const listInvitations = (): Promise<{ invitations: TeamInvitation[] }> =>
  request<{ invitations: TeamInvitation[] }>('GET', '/team/invitations');

export const createInvitation = (input: {
  email: string;
  roleId: string;
  facilityScopeIds?: string[] | null;
}): Promise<TeamInvitation> =>
  request<TeamInvitation>('POST', '/team/invitations', { body: input });

export const resendInvitation = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('POST', `/team/invitations/${id}/resend`, { body: {} });

export const revokeInvitation = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/team/invitations/${id}`);

// ----- Billing
export interface SaasPlanDescriptor {
  tier: 'starter' | 'pro' | 'enterprise';
  name: string;
  monthlyMinor: number;
  currency: string;
  unitDescription: string;
  features: string[];
  priceId: string | null;
}

export const getSaasPlans = (): Promise<{ plans: SaasPlanDescriptor[]; configured: boolean }> =>
  request<{ plans: SaasPlanDescriptor[]; configured: boolean }>('GET', '/billing/saas-plans');

export const getBillingStatus = (): Promise<BillingStatus> =>
  request<BillingStatus>('GET', '/billing/status');
export const startCheckout = (
  priceId: string,
  successUrl?: string,
  cancelUrl?: string,
): Promise<{ url: string }> =>
  request<{ url: string }>('POST', '/billing/checkout-session', {
    body: {
      priceId,
      ...(successUrl ? { successUrl } : {}),
      ...(cancelUrl ? { cancelUrl } : {}),
    },
  });
export const openCustomerPortal = (returnUrl?: string): Promise<{ url: string }> =>
  request<{ url: string }>('POST', '/billing/customer-portal', {
    body: { ...(returnUrl ? { returnUrl } : {}) },
  });
export const syncSubscription = (): Promise<{ status: string }> =>
  request<{ status: string }>('POST', '/billing/sync', { body: {} });

// ----- Stripe Connect (sub-spec 02 Plan 1 M5)
export const postConnectOnboardingLink = (): Promise<{ url: string }> =>
  request<{ url: string }>('POST', '/billing/connect/onboarding-link', { body: {} });

// ----- Branding
export const getBranding = (): Promise<BrandJson> => request<BrandJson>('GET', '/branding');
export const updateBranding = (patch: Partial<BrandJson>): Promise<BrandJson> =>
  request<BrandJson>('PATCH', '/branding', { body: patch });

// ----- Pricing quote (sub-spec 02 Plan 2 M8)
/** Call the pure quote engine for a single service. Returns the full price breakdown. */
export const postQuote = (input: QuoteInput): Promise<{ quote: Quote }> =>
  request<{ quote: Quote }>('POST', '/pricing/quote', { body: input });

// ----- Pricing rules (sub-spec 02 Plan 2 M6)
export const listPricingRules = (serviceId?: string): Promise<{ rules: PricingRule[] }> =>
  request<{ rules: PricingRule[] }>('GET', '/pricing/rules', {
    query: serviceId ? { service_id: serviceId } : {},
  });

export const createPricingRule = (input: CreatePricingRuleInput): Promise<{ rule: PricingRule }> =>
  request<{ rule: PricingRule }>('POST', '/pricing/rules', { body: input });

export const updatePricingRule = (
  id: string,
  patch: UpdatePricingRuleInput,
): Promise<{ rule: PricingRule }> =>
  request<{ rule: PricingRule }>('PATCH', `/pricing/rules/${id}`, { body: patch });

export const deletePricingRule = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/pricing/rules/${id}`);

// ----- Cancellation policies (sub-spec 02 Plan 2 M7)
export const getCancellationPolicies = (): Promise<CancellationPoliciesResponse> =>
  request<CancellationPoliciesResponse>('GET', '/cancellation-policies');

export const upsertCancellationPolicy = (
  input: UpsertCancellationPolicyInput,
): Promise<{ policy: CancellationPolicy }> =>
  request<{ policy: CancellationPolicy }>('POST', '/cancellation-policies', { body: input });

export const deleteCancellationPolicy = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/cancellation-policies/${id}`);

// ----- Discount codes (sub-spec 02 Plan 4 M6)
export const listDiscountCodes = (): Promise<{ codes: DiscountCode[] }> =>
  request<{ codes: DiscountCode[] }>('GET', '/discount-codes');

export const createDiscountCode = (
  input: CreateDiscountCodeInput,
): Promise<{ code: DiscountCode }> =>
  request<{ code: DiscountCode }>('POST', '/discount-codes', { body: input });

export const updateDiscountCode = (
  id: string,
  patch: UpdateDiscountCodeInput,
): Promise<{ code: DiscountCode }> =>
  request<{ code: DiscountCode }>('PATCH', `/discount-codes/${id}`, { body: patch });

export const deactivateDiscountCode = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/discount-codes/${id}`);

// ----- Custom fields (sub-spec 06 Plan 1)
export type CustomFieldScope = 'customer' | 'pet' | 'booking';
export type CustomFieldKind = 'text' | 'number' | 'date' | 'boolean' | 'select';

export interface CustomFieldDefinition {
  id: string;
  accountId: string;
  scope: CustomFieldScope;
  label: string;
  kind: CustomFieldKind;
  optionsJson: string | null;
  required: number;
  position: number;
  isActive: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomFieldDefinitionInput {
  scope: CustomFieldScope;
  label: string;
  kind: CustomFieldKind;
  options?: string[];
  required?: boolean;
  position?: number;
}

export interface UpdateCustomFieldDefinitionInput {
  label?: string;
  required?: boolean;
  position?: number;
}

export interface CustomFieldRenderDefinition {
  id: string;
  scope: CustomFieldScope;
  label: string;
  kind: CustomFieldKind;
  options: string[] | null;
  required: boolean;
  position: number;
}

export interface CustomFieldRenderRow {
  definition: CustomFieldRenderDefinition;
  value: string | number | boolean | null;
}

export const listCustomFieldDefinitions = (
  scope?: CustomFieldScope,
  activeOnly?: boolean,
): Promise<{ definitions: CustomFieldDefinition[] }> => {
  const params = new URLSearchParams();
  if (scope) params.set('scope', scope);
  if (activeOnly) params.set('activeOnly', '1');
  const qs = params.toString();
  return request<{ definitions: CustomFieldDefinition[] }>(
    'GET',
    `/custom-fields/definitions${qs ? `?${qs}` : ''}`,
  );
};

export const createCustomFieldDefinition = (
  input: CreateCustomFieldDefinitionInput,
): Promise<{ definition: CustomFieldDefinition }> =>
  request<{ definition: CustomFieldDefinition }>('POST', '/custom-fields/definitions', {
    body: input,
  });

export const updateCustomFieldDefinition = (
  id: string,
  patch: UpdateCustomFieldDefinitionInput,
): Promise<{ definition: CustomFieldDefinition }> =>
  request<{ definition: CustomFieldDefinition }>('PATCH', `/custom-fields/definitions/${id}`, {
    body: patch,
  });

export const deactivateCustomFieldDefinition = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/custom-fields/definitions/${id}`);

export const getCustomFieldValues = (
  scope: CustomFieldScope,
  entityId: string,
): Promise<{ fields: CustomFieldRenderRow[] }> =>
  request<{ fields: CustomFieldRenderRow[] }>('GET', `/custom-fields/values/${scope}/${entityId}`);

export const putCustomFieldValue = (
  definitionId: string,
  entityId: string,
  value: string | number | boolean | null,
): Promise<{ value: unknown }> =>
  request<{ value: unknown }>('PUT', '/custom-fields/values', {
    body: { definitionId, entityId, value },
  });

// ----- Intake questions (sub-spec 06 Plan 3)
export type IntakeQuestionKind = 'boolean' | 'acknowledgement' | 'select' | 'text' | 'number';
export type IntakeQuestionCategory = 'boarding' | 'daycare' | 'grooming';

export interface IntakeQuestion {
  id: string;
  accountId: string;
  slug: string;
  label: string;
  kind: IntakeQuestionKind;
  optionsJson: string | null;
  required: number;
  isAcknowledgement: number;
  appliesToCategories: string; // JSON-encoded array
  appliesWhenMultiPet: number;
  appliesWhenDogPresent: number;
  position: number;
  isActive: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIntakeQuestionInput {
  slug?: string;
  label: string;
  kind?: IntakeQuestionKind;
  options?: string[];
  required?: boolean;
  isAcknowledgement?: boolean;
  appliesToCategories?: IntakeQuestionCategory[];
  appliesWhenMultiPet?: boolean;
  appliesWhenDogPresent?: boolean;
  position?: number;
}

export interface UpdateIntakeQuestionInput {
  label?: string;
  kind?: IntakeQuestionKind;
  options?: string[] | null;
  required?: boolean;
  isAcknowledgement?: boolean;
  appliesToCategories?: IntakeQuestionCategory[];
  appliesWhenMultiPet?: boolean;
  appliesWhenDogPresent?: boolean;
  position?: number;
}

export const listIntakeQuestions = (
  activeOnly?: boolean,
): Promise<{ questions: IntakeQuestion[] }> => {
  const qs = activeOnly ? '?activeOnly=1' : '';
  return request<{ questions: IntakeQuestion[] }>('GET', `/intake-questions${qs}`);
};

export const createIntakeQuestion = (
  body: CreateIntakeQuestionInput,
): Promise<{ question: IntakeQuestion }> =>
  request<{ question: IntakeQuestion }>('POST', '/intake-questions', { body });

export const updateIntakeQuestion = (
  id: string,
  patch: UpdateIntakeQuestionInput,
): Promise<{ question: IntakeQuestion }> =>
  request<{ question: IntakeQuestion }>('PATCH', `/intake-questions/${id}`, { body: patch });

export const deactivateIntakeQuestion = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/intake-questions/${id}`);

export const reorderIntakeQuestions = (
  items: Array<{ id: string; position: number }>,
): Promise<{ ok: true }> =>
  request<{ ok: true }>('POST', '/intake-questions/reorder', { body: items });

// ----- Daycare membership tiers (sub-spec 02 Plan 5 M5)
export const listMembershipTiers = (): Promise<{ tiers: MembershipTier[] }> =>
  request<{ tiers: MembershipTier[] }>('GET', '/memberships/tiers');

export const createMembershipTier = (
  input: CreateMembershipTierInput,
): Promise<{ tier: MembershipTier }> =>
  request<{ tier: MembershipTier }>('POST', '/memberships/tiers', { body: input });

export const updateMembershipTier = (
  id: string,
  patch: UpdateMembershipTierInput,
): Promise<{ tier: MembershipTier }> =>
  request<{ tier: MembershipTier }>('PATCH', `/memberships/tiers/${id}`, { body: patch });

// ----- Custom hostname
export const getCustomHostname = (): Promise<CustomHostnameState> =>
  request<CustomHostnameState>('GET', '/branding/custom-hostname');
export const addCustomHostname = (hostname: string): Promise<CustomHostnameState> =>
  request<CustomHostnameState>('POST', '/branding/custom-hostname', {
    body: { hostname },
  });
export const removeCustomHostname = (): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>('DELETE', '/branding/custom-hostname');

// ----- Sending domain (sub-spec 03 Plan 2 M6)
export const getSendingDomain = (): Promise<SendingDomainState> =>
  request<SendingDomainState>('GET', '/branding/sending-domain');
export const addSendingDomain = (domain: string): Promise<SendingDomainState> =>
  request<SendingDomainState>('POST', '/branding/sending-domain', { body: { domain } });
export const verifySendingDomain = (): Promise<SendingDomainState> =>
  request<SendingDomainState>('POST', '/branding/sending-domain/verify');
export const removeSendingDomain = (): Promise<{ status: string }> =>
  request<{ status: string }>('DELETE', '/branding/sending-domain');

// ----- SMS / WhatsApp provider (sub-spec 03 Plan 3 M5+M6)
export const getSmsProvider = (): Promise<SmsProviderState> =>
  request<SmsProviderState>('GET', '/billing/sms-provider');
export const upsertSmsProvider = (input: SmsProviderUpsertInput): Promise<SmsProviderState> =>
  request<SmsProviderState>('POST', '/billing/sms-provider', { body: input });
export const removeSmsProvider = (): Promise<{ twilioStatus: string }> =>
  request<{ twilioStatus: string }>('DELETE', '/billing/sms-provider');

// ----- Refund requests (sub-spec 02 Plan 6 M7)

export const listRefundRequests = (
  status?: RefundStatus,
): Promise<{ refund_requests: EnrichedRefundRequest[] }> =>
  request<{ refund_requests: EnrichedRefundRequest[] }>('GET', '/refund-requests', {
    query: status ? { status } : {},
  });

export const approveRefundRequest = (
  id: string,
  args: ApproveRefundRequestArgs,
): Promise<{ refund_request: RefundRequest }> =>
  request<{ refund_request: RefundRequest }>('PATCH', `/refund-requests/${id}`, {
    body: { action: 'approve', ...args },
  });

export const rejectRefundRequest = (
  id: string,
  staffNote?: string,
): Promise<{ refund_request: RefundRequest }> =>
  request<{ refund_request: RefundRequest }>('PATCH', `/refund-requests/${id}`, {
    body: { action: 'reject', ...(staffNote ? { staff_note: staffNote } : {}) },
  });

export const staffInitiateRefund = (args: {
  payment_id: string;
  amount_minor: number;
  reason: string;
  source_override?: 'cash' | 'check' | 'credit';
  cheque_no?: string;
}): Promise<{ refund_request: RefundRequest; executed: unknown }> =>
  request<{ refund_request: RefundRequest; executed: unknown }>('POST', '/refunds', { body: args });

// ----- POS products (sub-spec 02 Plan 7 M5)

export const listPosProducts = (activeOnly = true): Promise<{ products: PosProduct[] }> =>
  request<{ products: PosProduct[] }>('GET', '/pos/products', {
    query: activeOnly ? {} : { active: '0' },
  });

export const createPosProduct = (input: CreatePosProductInput): Promise<{ product: PosProduct }> =>
  request<{ product: PosProduct }>('POST', '/pos/products', { body: input });

export const updatePosProduct = (
  id: string,
  patch: UpdatePosProductInput,
): Promise<{ product: PosProduct }> =>
  request<{ product: PosProduct }>('PATCH', `/pos/products/${id}`, { body: patch });

export const deletePosProduct = (id: string): Promise<void> =>
  request<void>('DELETE', `/pos/products/${id}`);

// ----- POS stock management (migration 0046)

export const listPosStockMovements = (params?: {
  product_id?: string;
  facility_id?: string;
  cursor?: string;
  limit?: number;
}): Promise<{ movements: PosStockMovement[]; next_cursor: string | null }> => {
  const query: Record<string, string> = {};
  if (params?.product_id) query.product_id = params.product_id;
  if (params?.facility_id) query.facility_id = params.facility_id;
  if (params?.cursor) query.cursor = params.cursor;
  if (params?.limit !== undefined) query.limit = String(params.limit);
  return request<{ movements: PosStockMovement[]; next_cursor: string | null }>(
    'GET',
    '/pos/stock-movements',
    { query },
  );
};

export const getPosStockAlerts = (
  facilityId?: string,
): Promise<{ alerts: Array<{ product: PosProduct; deficit: number }> }> =>
  request<{ alerts: Array<{ product: PosProduct; deficit: number }> }>('GET', '/pos/stock-alerts', {
    query: facilityId ? { facility_id: facilityId } : {},
  });

export const posProductRestock = (
  id: string,
  body: { qty: number; unit_cost_minor?: number | null; notes?: string | null },
): Promise<{ movement: PosStockMovement; qty_after: number }> =>
  request<{ movement: PosStockMovement; qty_after: number }>(
    'POST',
    `/pos/products/${id}/restock`,
    { body },
  );

export const posProductAdjust = (
  id: string,
  body: { change_qty: number; notes?: string | null },
): Promise<{ movement: PosStockMovement; qty_after: number }> =>
  request<{ movement: PosStockMovement; qty_after: number }>('POST', `/pos/products/${id}/adjust`, {
    body,
  });

export const posProductWastage = (
  id: string,
  body: { qty: number; reason?: string | null; notes?: string | null },
): Promise<{ movement: PosStockMovement; qty_after: number }> =>
  request<{ movement: PosStockMovement; qty_after: number }>(
    'POST',
    `/pos/products/${id}/wastage`,
    { body },
  );

// ----- POS sessions (sub-spec 02 Plan 7 M8)

export const getCurrentPosSession = (
  facility_id: string,
): Promise<{ session: PosSession | null }> =>
  request<{ session: PosSession | null }>('GET', '/pos/sessions/current', {
    query: { facility_id },
  });

export const openPosSession = (body: {
  facility_id: string;
  opening_float_minor: number;
}): Promise<{ session: PosSession }> =>
  request<{ session: PosSession }>('POST', '/pos/sessions/open', { body });

export const closePosSession = (
  id: string,
  body: { closing_float_minor: number; notes?: string },
): Promise<{ session: PosSession; expected_cash_minor: number; cash_variance_minor: number }> =>
  request<{ session: PosSession; expected_cash_minor: number; cash_variance_minor: number }>(
    'POST',
    `/pos/sessions/${id}/close`,
    { body },
  );

// ----- POS overhaul (migration 0074)

export const broadcastPosCart = (body: {
  session_id: string;
  kind: 'cart' | 'completed';
  cart_lines: Array<{ description: string; quantity: number; unit_price_minor: number }>;
  transaction_id: string | null;
}): Promise<{ ok: boolean }> => request<{ ok: boolean }>('POST', '/pos/cart-broadcast', { body });

export const reprintPosTransaction = (
  id: string,
): Promise<{
  transaction: { id: string; total_minor: number; currency: string };
  lines: Array<{ description: string; quantity: number; line_total_minor: number }>;
  payments: Array<{ source: string; amount_minor: number }>;
  reprint_count: number;
}> => request('POST', `/pos/transactions/${id}/reprint`);

export const applyPosLoyaltyPoints = (
  id: string,
  points: number,
): Promise<{
  payment: { id: string; amount_minor: number };
  points_redeemed: number;
  value_minor: number;
  transaction_closed: boolean;
}> => request('POST', `/pos/transactions/${id}/apply-loyalty`, { body: { points } });

export const getLowStockProducts = (): Promise<{
  products: Array<{
    product: { id: string; name: string; stockQuantity: number; lowStockThreshold: number | null };
    deficit: number;
  }>;
}> => request('GET', '/pos/low-stock');

export interface PosSupplier {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  defaultLeadDays: number;
  active: number;
}

export const listPosSuppliers = (includeInactive = false): Promise<{ suppliers: PosSupplier[] }> =>
  request('GET', '/pos/suppliers', { query: includeInactive ? { include_inactive: '1' } : {} });

export const createPosSupplier = (body: {
  name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  default_lead_days?: number;
}): Promise<{ supplier: PosSupplier }> => request('POST', '/pos/suppliers', { body });

export const updatePosSupplier = (
  id: string,
  body: {
    name?: string;
    email?: string | null;
    phone?: string | null;
    notes?: string | null;
    default_lead_days?: number;
    active?: boolean;
  },
): Promise<{ supplier: PosSupplier }> => request('PATCH', `/pos/suppliers/${id}`, { body });

export const deactivatePosSupplier = (id: string): Promise<{ ok: boolean }> =>
  request('DELETE', `/pos/suppliers/${id}`);

// ----- POS transactions (sub-spec 02 Plan 7 M8)

export interface CreatePosTransactionInput {
  session_id: string;
  customer_id?: string | null;
  booking_id?: string | null;
  lines: Array<{
    kind: 'product' | 'service' | 'manual';
    pos_product_id?: string | null;
    service_id?: string | null;
    description: string;
    quantity: number;
    unit_price_minor: number;
    taxable: boolean;
    position: number;
  }>;
}

export const createPosTransaction = (
  input: CreatePosTransactionInput,
): Promise<{ transaction: PosTransaction; lines: PosTransactionLine[] }> =>
  request<{ transaction: PosTransaction; lines: PosTransactionLine[] }>(
    'POST',
    '/pos/transactions',
    { body: input },
  );

export interface PayPosTransactionBody {
  source: 'cash' | 'stripe_card_manual' | 'check' | 'account_credit' | 'gift_card';
  amount_minor: number;
  cheque_no?: string;
  stripe_payment_method_id?: string;
  /** Required when source='gift_card' — the card code to redeem against. */
  gift_card_code?: string;
  /** Migration 0098 — gratuity added on top of amount_minor (card sources only). */
  tip_minor?: number;
}

// ----- Text-to-pay payment links + tips (migration 0098)

export type PaymentLinkPurpose = 'booking_balance' | 'booking_deposit' | 'pos' | 'custom';
export type PaymentLinkStatus = 'open' | 'paid' | 'expired' | 'cancelled';

export interface PaymentLinkRow {
  id: string;
  status: PaymentLinkStatus;
  purpose: PaymentLinkPurpose;
  amountMinor: number;
  currency: string;
  bookingId: string | null;
  posTransactionId: string | null;
  customerId: string;
  description: string | null;
  expiresAt: string;
  paidAt: string | null;
  tipMinor: number;
  sentViaJson: string | null;
  createdAt: string;
}

export interface PaymentLinkResult {
  link: PaymentLinkRow;
  url: string;
  expiresAt: string;
  sent: boolean;
  channels: string[];
  supersededLinkIds: string[];
}

export type PaymentLinkChannel = 'email' | 'sms' | 'whatsapp';

export interface CreatePaymentLinkInput {
  purpose: PaymentLinkPurpose;
  bookingId?: string;
  posTransactionId?: string;
  customerId?: string;
  amountMinor?: number;
  description?: string;
  send?: PaymentLinkChannel[];
}

export const createPaymentLink = (body: CreatePaymentLinkInput) =>
  request<PaymentLinkResult>('POST', '/payment-links', { body });

export const listPaymentLinks = (query: {
  bookingId?: string;
  posTransactionId?: string;
  customerId?: string;
  status?: PaymentLinkStatus;
}) => request<{ links: PaymentLinkRow[] }>('GET', '/payment-links', { query });

export const cancelPaymentLink = (id: string) =>
  request<{ link: PaymentLinkRow }>('POST', `/payment-links/${id}/cancel`, { body: {} });

export interface TipSettings {
  tipsEnabled: boolean;
  /** Whole-number percentages offered as chips, in display order. */
  tipPresets: number[];
  allowCustomTip: boolean;
}

export const getTipSettings = () => request<{ settings: TipSettings }>('GET', '/tip-settings');

export const updateTipSettings = (body: Partial<TipSettings>) =>
  request<{ settings: TipSettings }>('PUT', '/tip-settings', { body });

// ----- POS sessions list + detail (sub-spec 02 Plan 7 M9)

export const listPosSessions = (filter?: {
  status?: 'open' | 'closed';
  facility_id?: string;
}): Promise<{ sessions: PosSession[] }> =>
  request<{ sessions: PosSession[] }>('GET', '/pos/sessions', {
    query: filter ?? {},
  });

export const getPosSessionDetail = (
  id: string,
): Promise<{
  session: PosSession;
  transactions: PosTransaction[];
  totals_by_source: Record<string, number>;
}> =>
  request<{
    session: PosSession;
    transactions: PosTransaction[];
    totals_by_source: Record<string, number>;
  }>('GET', `/pos/sessions/${id}`);

export const payPosTransaction = (
  id: string,
  body: PayPosTransactionBody,
): Promise<{
  payment: PosPayment;
  payment_intent?: {
    client_secret: string;
    stripe_account_id: string;
    payment_intent_id: string;
  };
  transaction_closed: boolean;
}> =>
  request<{
    payment: PosPayment;
    payment_intent?: {
      client_secret: string;
      stripe_account_id: string;
      payment_intent_id: string;
    };
    transaction_closed: boolean;
  }>('POST', `/pos/transactions/${id}/pay`, { body });

// ----- Notification templates (sub-spec 03 Plan 5 M2)

export const listNotificationTemplates = (): Promise<{ templates: NotificationTemplate[] }> =>
  request<{ templates: NotificationTemplate[] }>('GET', '/notification-templates');

export const getNotificationTemplate = (
  key: string,
  channel: string,
  locale: string,
): Promise<{ current: NotificationTemplate | null; versions: TemplateVersionSummary[] }> =>
  request<{ current: NotificationTemplate | null; versions: TemplateVersionSummary[] }>(
    'GET',
    `/notification-templates/${encodeURIComponent(key)}/${encodeURIComponent(channel)}/${encodeURIComponent(locale)}`,
  );

export const saveNotificationTemplate = (
  input: SaveTemplateInput,
): Promise<{ template: NotificationTemplate }> =>
  request<{ template: NotificationTemplate }>('POST', '/notification-templates', { body: input });

export const listNotificationTemplateVersions = (
  id: string,
): Promise<{ versions: NotificationTemplate[] }> =>
  request<{ versions: NotificationTemplate[] }>('GET', `/notification-templates/${id}/versions`);

export const restoreNotificationTemplateVersion = (
  id: string,
): Promise<{ template: NotificationTemplate }> =>
  request<{ template: NotificationTemplate }>('POST', `/notification-templates/${id}/restore`, {
    body: {},
  });

// ----- Comm deliveries audit (sub-spec 03 Plan 5 M3)

export const listCommDeliveries = (
  filters: {
    status?: CommDeliveryStatus | undefined;
    trigger?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    cursor?: string | undefined;
  } = {},
): Promise<{ deliveries: CommDelivery[]; nextCursor: string | null }> =>
  request<{ deliveries: CommDelivery[]; nextCursor: string | null }>('GET', '/comm-deliveries', {
    query: filters,
  });

export const getCommDelivery = (id: string): Promise<{ delivery: CommDelivery }> =>
  request<{ delivery: CommDelivery }>('GET', `/comm-deliveries/${id}`);

// ----- Comm failed deliveries (sub-spec 03 Plan 5 M4)

export const listCommFailures = (
  filters: {
    failure_class?: CommFailureClass | undefined;
    ack?: 'true' | 'false' | undefined;
    cursor?: string | undefined;
  } = {},
): Promise<{ failures: CommFailure[]; nextCursor: string | null }> =>
  request<{ failures: CommFailure[]; nextCursor: string | null }>(
    'GET',
    '/comm-failed-deliveries',
    { query: filters },
  );

export const retryCommFailure = (id: string): Promise<{ deliveryId: string }> =>
  request<{ deliveryId: string }>('POST', `/comm-failed-deliveries/${id}/retry`, { body: {} });

export const acknowledgeCommFailure = (id: string): Promise<{ failure: CommFailure }> =>
  request<{ failure: CommFailure }>('POST', `/comm-failed-deliveries/${id}/acknowledge`, {
    body: {},
  });

// ----- Daycare settings (sub-spec 04 Plan 5)

export interface DaycareCapacityRow {
  id: string;
  facilityId: string;
  dayOfWeek: number | null;
  sessionType: string;
  maxDogs: number;
  maxBySize: string | null;
  createdAt: string;
  updatedAt: string;
}

export const listDaycareCapacity = (): Promise<{ rows: DaycareCapacityRow[] }> =>
  request<{ rows: DaycareCapacityRow[] }>('GET', '/daycare/capacity');

export const upsertDaycareCapacity = (input: {
  facilityId: string;
  dayOfWeek: number;
  sessionType: string;
  maxDogs: number;
  maxBySize?: Record<string, number> | null;
}): Promise<{ row: DaycareCapacityRow }> =>
  request<{ row: DaycareCapacityRow }>('POST', '/daycare/capacity', { body: input });

export const deleteDaycareCapacity = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/daycare/capacity/${id}`);

export interface LatePickupPolicyDto {
  enabled: boolean;
  cutoffHour: number;
  intervalMinutes: number;
  perIntervalMinor: number;
  currency: string;
  graceMinutes?: number;
}

export const getLatePickupPolicy = (): Promise<{ policy: LatePickupPolicyDto | null }> =>
  request<{ policy: LatePickupPolicyDto | null }>('GET', '/daycare/late-pickup-policy');

export const putLatePickupPolicy = (
  body: LatePickupPolicyDto,
): Promise<{ policy: LatePickupPolicyDto }> =>
  request<{ policy: LatePickupPolicyDto }>('PUT', '/daycare/late-pickup-policy', { body });

export const deleteLatePickupPolicy = (): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', '/daycare/late-pickup-policy');

// --- Boarding overstay policy (migration 0103) -----------------------------

export type OverstayChargeMode = 'nightly_rate' | 'flat_fee' | 'nightly_rate_plus_fee';

export interface OverstayPolicyDto {
  enabled: boolean;
  /** Facility-local hour (0-23) on the scheduled checkout day. */
  checkoutCutoffHour: number;
  /** 0-720. */
  graceMinutes: number;
  chargeMode: OverstayChargeMode;
  /** Flat component, minor units in the account currency. */
  feeMinor: number;
  autoCharge: boolean;
  notifyCustomer: boolean;
}

export interface OverstayPolicyResponse {
  policy: OverstayPolicyDto;
  /** false = tenant has never saved a row (platform default, OFF). */
  configured: boolean;
  currency: string;
}

export const getOverstayPolicy = (): Promise<OverstayPolicyResponse> =>
  request<OverstayPolicyResponse>('GET', '/overstay-policy');

export const putOverstayPolicy = (body: OverstayPolicyDto): Promise<OverstayPolicyResponse> =>
  request<OverstayPolicyResponse>('PUT', '/overstay-policy', { body });

export const deleteOverstayPolicy = (): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', '/overstay-policy');

export type DaycareBurnOrder = 'pack_first' | 'membership_first';

export const getDaycareBurnOrder = (): Promise<{ order: DaycareBurnOrder }> =>
  request<{ order: DaycareBurnOrder }>('GET', '/daycare/burn-order');

export const putDaycareBurnOrder = (
  order: DaycareBurnOrder,
): Promise<{ order: DaycareBurnOrder }> =>
  request<{ order: DaycareBurnOrder }>('PUT', '/daycare/burn-order', { body: { order } });

// ─── Vaccines catalogue (sub-spec 06 — vaccines entity) ──────────────────

export interface Vaccine {
  id: string;
  accountId: string;
  slug: string;
  name: string;
  speciesAllowed: string; // JSON e.g. '["dog","cat"]'
  description: string | null;
  isRequiredForBoarding: number;
  isRequiredForDaycare: number;
  isRequiredForGrooming: number;
  typicalValidityMonths: number | null;
  isCore: number;
  position: number;
  isActive: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVaccineBody {
  slug: string;
  name: string;
  speciesAllowed: ('dog' | 'cat')[];
  description?: string | null;
  typicalValidityMonths?: number | null;
  isCore?: boolean;
  position?: number;
}

export interface UpdateVaccineBody {
  name?: string;
  speciesAllowed?: ('dog' | 'cat')[];
  description?: string | null;
  typicalValidityMonths?: number | null;
  isCore?: boolean;
  isActive?: boolean;
  position?: number;
}

export const listVaccines = (activeOnly = true): Promise<{ vaccines: Vaccine[] }> =>
  request<{ vaccines: Vaccine[] }>('GET', `/vaccines${activeOnly ? '' : '?active=false'}`);

export const createVaccine = (body: CreateVaccineBody): Promise<{ vaccine: Vaccine }> =>
  request<{ vaccine: Vaccine }>('POST', '/vaccines', { body });

export const getVaccine = (id: string): Promise<{ vaccine: Vaccine }> =>
  request<{ vaccine: Vaccine }>('GET', `/vaccines/${id}`);

export const updateVaccine = (id: string, body: UpdateVaccineBody): Promise<{ vaccine: Vaccine }> =>
  request<{ vaccine: Vaccine }>('PATCH', `/vaccines/${id}`, { body });

export const deactivateVaccine = (id: string): Promise<{ vaccine: Vaccine }> =>
  request<{ vaccine: Vaccine }>('DELETE', `/vaccines/${id}`);

export const deleteVaccinePermanently = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/vaccines/${id}/permanent`);

export const loadStandardVaccineLibrary = (): Promise<{
  inserted: number;
  skipped: number;
  /** Default (species × category) policies written for vaccines that had none. */
  policiesInserted: number;
}> =>
  request<{ inserted: number; skipped: number; policiesInserted: number }>(
    'POST',
    '/vaccines/load-standard-library',
  );

// ─── Vaccine policies + overrides (sub-spec 06 Plan 2) ────────────────────

export type VaccineServiceCategory = 'boarding' | 'daycare' | 'grooming';
export type PetSpecies = 'dog' | 'cat';
export const PET_SPECIES: readonly PetSpecies[] = ['dog', 'cat'];

export interface VaccinePolicy {
  id: string;
  accountId: string;
  /** The pet type this policy governs — the gate only applies it to pets of this species. */
  species: PetSpecies;
  serviceCategory: VaccineServiceCategory;
  vaccineId: string;
  required: number;
  gracePeriodDays: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVaccinePolicyBody {
  species: PetSpecies;
  serviceCategory: VaccineServiceCategory;
  vaccineId: string;
  required?: boolean;
  gracePeriodDays?: number;
}

export interface UpdateVaccinePolicyBody {
  required?: boolean;
  gracePeriodDays?: number;
}

export const listVaccinePolicies = (
  category?: VaccineServiceCategory,
  includeAdvisory?: boolean,
  species?: PetSpecies,
): Promise<{ policies: VaccinePolicy[] }> => {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (includeAdvisory) params.set('includeAdvisory', 'true');
  if (species) params.set('species', species);
  const qs = params.toString();
  return request<{ policies: VaccinePolicy[] }>('GET', `/vaccine-policies${qs ? `?${qs}` : ''}`);
};

export const createVaccinePolicy = (
  body: CreateVaccinePolicyBody,
): Promise<{ policy: VaccinePolicy }> =>
  request<{ policy: VaccinePolicy }>('POST', '/vaccine-policies', { body });

export const updateVaccinePolicy = (
  id: string,
  body: UpdateVaccinePolicyBody,
): Promise<{ policy: VaccinePolicy }> =>
  request<{ policy: VaccinePolicy }>('PATCH', `/vaccine-policies/${id}`, { body });

export const deleteVaccinePolicy = (id: string): Promise<void> =>
  request<void>('DELETE', `/vaccine-policies/${id}`);

export interface VaccineOverride {
  id: string;
  accountId: string;
  petId: string;
  vaccineId: string;
  /** Enriched by the GET /vaccine-overrides list handler. Falls back to petId if pet not found. */
  petName: string;
  /** Enriched by the GET /vaccine-overrides list handler. Falls back to vaccineId if vaccine not found. */
  vaccineName: string;
  grantedByMembershipId: string | null;
  reason: string;
  validUntil: string | null;
  usedAtBookingId: string | null;
  revokedAt: string | null;
  revokedByMembershipId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type VaccineOverrideStatus = 'active' | 'expired' | 'revoked';

export interface CreateVaccineOverrideBody {
  petId: string;
  vaccineId: string;
  reason: string;
  validUntil?: string | null;
}

export const listVaccineOverrides = (
  status: VaccineOverrideStatus = 'active',
): Promise<{ overrides: VaccineOverride[] }> =>
  request<{ overrides: VaccineOverride[] }>('GET', `/vaccine-overrides?status=${status}`);

export const createVaccineOverride = (
  body: CreateVaccineOverrideBody,
): Promise<{ override: VaccineOverride }> =>
  request<{ override: VaccineOverride }>('POST', '/vaccine-overrides', { body });

export const revokeVaccineOverride = (id: string): Promise<{ override: VaccineOverride }> =>
  request<{ override: VaccineOverride }>('PATCH', `/vaccine-overrides/${id}/revoke`);

// -- Sub-spec 06 Plan 5: run card template + belongings -------------------

export interface BookingBelonging {
  id: string;
  bookingId: string;
  description: string;
  photoR2Key: string | null;
  /** Sub-spec 07 Plan 3 — FK into files table; populated by photo upload flow. */
  photoFileId: string | null;
  recordedAt: string;
  recordedByMembershipId: string | null;
  returnedAt: string | null;
  returnedByMembershipId: string | null;
  createdAt: string;
  updatedAt: string;
}

export const listBookingBelongings = (bookingId: string): Promise<{ items: BookingBelonging[] }> =>
  request<{ items: BookingBelonging[] }>('GET', `/bookings/${bookingId}/belongings`);

export const createBookingBelonging = (
  bookingId: string,
  body: { description: string; photoR2Key?: string | null; photoFileId?: string | null },
): Promise<{ item: BookingBelonging }> =>
  request<{ item: BookingBelonging }>('POST', `/bookings/${bookingId}/belongings`, { body });

export const updateBookingBelonging = (
  bookingId: string,
  itemId: string,
  body: { description?: string; photoR2Key?: string | null; photoFileId?: string | null },
): Promise<{ item: BookingBelonging }> =>
  request<{ item: BookingBelonging }>('PATCH', `/bookings/${bookingId}/belongings/${itemId}`, {
    body,
  });

export const deleteBookingBelonging = (bookingId: string, itemId: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/bookings/${bookingId}/belongings/${itemId}`);

export const markBookingBelongingReturned = (
  bookingId: string,
  itemId: string,
): Promise<{ item: BookingBelonging }> =>
  request<{ item: BookingBelonging }>(
    'PATCH',
    `/bookings/${bookingId}/belongings/${itemId}/return`,
  );

export const getRunCardTemplate = (): Promise<{ html: string | null }> =>
  request<{ html: string | null }>('GET', '/branding/run-card-template');

export const putRunCardTemplate = (html: string | null): Promise<{ html: string | null }> =>
  request<{ html: string | null }>('PUT', '/branding/run-card-template', { body: { html } });

// Task #27 — run card layout + footer + batch + QR.
export type RunCardLayout = 'compact' | 'full' | 'poster';

export const getRunCardSettings = (): Promise<{
  layout: RunCardLayout;
  footerText: string | null;
}> => request('GET', '/branding/run-card-settings');

export const putRunCardSettings = (patch: {
  layout?: RunCardLayout;
  footerText?: string | null;
}): Promise<{ layout: RunCardLayout; footerText: string | null }> =>
  request('PUT', '/branding/run-card-settings', { body: patch });

export const issueRunCardQrToken = (
  bookingId: string,
): Promise<{ token: string; url: string; expiresAt: number }> =>
  request('POST', '/run-cards/qr-token', { body: { bookingId } });

/**
 * Open the batched run card HTML in a new window and trigger print on load.
 * Returns the new-window reference so callers can null-check (popup blocker).
 */
export async function openBatchRunCardsForPrint(
  facilityId: string,
  date: string,
  layout: RunCardLayout,
): Promise<Window | null> {
  const params = new URLSearchParams({ facilityId, date, layout });
  const res = await fetch(`/api/run-cards/batch?${params.toString()}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`failed to fetch batched run cards: ${res.status}`);
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (win) {
    win.addEventListener('load', () => {
      setTimeout(() => {
        try {
          win.print();
        } catch {
          // Print blocked — operator can hit Cmd+P manually.
        }
      }, 250);
    });
  }
  return win;
}

// ---------------------------------------------------------------------------
// Sub-spec 11 Plan 4 — Waiver authoring (staff) + custom roles
// ---------------------------------------------------------------------------

export const getWaiver = (): Promise<{ html: string | null; version: number }> =>
  request<{ html: string | null; version: number }>('GET', '/waiver');

export const putWaiver = (html: string | null): Promise<{ html: string | null; version: number }> =>
  request<{ html: string | null; version: number }>('PUT', '/waiver', { body: { html } });

export interface RoleRow {
  id: string;
  slug: string;
  name: string;
  isBuiltin: boolean;
  version: number;
  capabilities: string[];
}

export const getAllRoles = (): Promise<RoleRow[]> => request<RoleRow[]>('GET', '/team/roles');

export const getAllCapabilities = (): Promise<{ capabilities: string[] }> =>
  request<{ capabilities: string[] }>('GET', '/team/capabilities');

export const createCustomRole = (input: {
  slug: string;
  name: string;
  capabilities: string[];
}): Promise<RoleRow> => request<RoleRow>('POST', '/team/roles', { body: input });

export const updateCustomRole = (
  id: string,
  patch: { name?: string; capabilities?: string[] },
): Promise<RoleRow> => request<RoleRow>('PATCH', `/team/roles/${id}`, { body: patch });

export const deleteCustomRole = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/team/roles/${id}`);

/**
 * Fetch the rendered run card HTML for a boarding booking and open it in a
 * new window, triggering print on load. Returns the new Window for tests.
 */
export async function openAndPrintRunCard(bookingId: string): Promise<Window | null> {
  const res = await fetch(`${API}/bookings/${bookingId}/run-card`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new ApiError('run_card_failed', res.status, await res.text().catch(() => null));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (win) {
    win.addEventListener('load', () => {
      try {
        win.print();
      } catch {
        // Some browsers throw if print() fires too early; ignore.
      }
    });
  }
  return win;
}

// =============================================================================
// Sub-spec 06 Plan 4 — booking statuses + task-board columns + tags
// =============================================================================

export interface AccountBookingStatus {
  id: string;
  accountId: string;
  parentBuiltIn: string;
  displayName: string;
  color: string | null;
  position: number;
  active: number;
}

export async function listBookingStatuses(opts?: {
  parentBuiltIn?: string;
  activeOnly?: boolean;
}): Promise<AccountBookingStatus[]> {
  const query: Record<string, string> = {};
  if (opts?.parentBuiltIn) query.parentBuiltIn = opts.parentBuiltIn;
  if (opts?.activeOnly) query.activeOnly = '1';
  const data = await request<{ statuses: AccountBookingStatus[] }>('GET', '/booking-statuses', {
    query,
  });
  return data.statuses;
}

export async function createBookingStatus(input: {
  parentBuiltIn: string;
  displayName: string;
  color?: string | null;
  position?: number;
}): Promise<AccountBookingStatus> {
  const data = await request<{ status: AccountBookingStatus }>('POST', '/booking-statuses', {
    body: input,
  });
  return data.status;
}

export async function updateBookingStatus(
  id: string,
  patch: { displayName?: string; color?: string | null; position?: number },
): Promise<AccountBookingStatus> {
  const data = await request<{ status: AccountBookingStatus }>('PATCH', `/booking-statuses/${id}`, {
    body: patch,
  });
  return data.status;
}

export async function deactivateBookingStatus(id: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/booking-statuses/${id}`);
}

export async function deleteBookingStatusPermanently(id: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/booking-statuses/${id}/permanent`);
}

export async function setBookingCustomStatus(
  bookingId: string,
  customStatusId: string | null,
): Promise<{ booking: { id: string; customStatusId: string | null } }> {
  return request('PATCH', `/bookings/${bookingId}/custom-status`, {
    body: { customStatusId },
  });
}

// --- Task-board columns ---

export interface TaskBoardColumn {
  id: string;
  accountId: string;
  name: string;
  filterJson: string;
  position: number;
  active: number;
}

export async function listTaskBoardColumns(opts?: {
  activeOnly?: boolean;
}): Promise<TaskBoardColumn[]> {
  const query: Record<string, string> = {};
  if (opts?.activeOnly) query.activeOnly = '1';
  const data = await request<{ columns: TaskBoardColumn[] }>('GET', '/task-board/columns', {
    query,
  });
  return data.columns;
}

export async function createTaskBoardColumn(input: {
  name: string;
  filter: unknown;
  position?: number;
}): Promise<TaskBoardColumn> {
  const data = await request<{ column: TaskBoardColumn }>('POST', '/task-board/columns', {
    body: input,
  });
  return data.column;
}

export async function updateTaskBoardColumn(
  id: string,
  patch: { name?: string; filter?: unknown; position?: number },
): Promise<TaskBoardColumn> {
  const data = await request<{ column: TaskBoardColumn }>('PATCH', `/task-board/columns/${id}`, {
    body: patch,
  });
  return data.column;
}

export async function deleteTaskBoardColumn(id: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/task-board/columns/${id}`);
}

export async function reorderTaskBoardColumns(
  items: Array<{ id: string; position: number }>,
): Promise<void> {
  await request<{ ok: true }>('PUT', '/task-board/columns/reorder', {
    body: { items },
  });
}

export interface TaskBoardBookingRow {
  booking: {
    id: string;
    customerId: string;
    petId: string;
    type: string;
    status: string;
    customStatusId: string | null;
    facilityId: string;
  };
  customer: { id: string; firstName: string; lastName: string } | null;
  pet: { id: string; name: string; species: string } | null;
  stay: { bookingId: string; checkIn: string; checkout: string } | null;
}

export async function listTaskBoardColumnBookings(
  columnId: string,
  opts?: { timezone?: string; facilityId?: string },
): Promise<TaskBoardBookingRow[]> {
  const query: Record<string, string> = {};
  if (opts?.timezone) query.timezone = opts.timezone;
  if (opts?.facilityId) query.facilityId = opts.facilityId;
  const data = await request<{ bookings: TaskBoardBookingRow[] }>(
    'GET',
    `/task-board/columns/${columnId}/bookings`,
    { query },
  );
  return data.bookings;
}

// --- Tags ---

export type TagScope = 'customer' | 'pet' | 'booking';

export interface AccountTag {
  id: string;
  accountId: string;
  name: string;
  color: string | null;
  scope: TagScope;
  active: number;
}

export async function listTags(opts?: {
  scope?: TagScope;
  activeOnly?: boolean;
}): Promise<AccountTag[]> {
  const query: Record<string, string> = {};
  if (opts?.scope) query.scope = opts.scope;
  if (opts?.activeOnly) query.activeOnly = '1';
  const data = await request<{ tags: AccountTag[] }>('GET', '/tags', { query });
  return data.tags;
}

export async function createTag(input: {
  name: string;
  scope: TagScope;
  color?: string | null;
}): Promise<AccountTag> {
  const data = await request<{ tag: AccountTag }>('POST', '/tags', { body: input });
  return data.tag;
}

export async function updateTag(
  id: string,
  patch: { name?: string; color?: string | null },
): Promise<AccountTag> {
  const data = await request<{ tag: AccountTag }>('PATCH', `/tags/${id}`, { body: patch });
  return data.tag;
}

export async function deactivateTag(id: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/tags/${id}`);
}

export async function deleteTagPermanently(id: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/tags/${id}/permanent`);
}

export interface TagAssignment {
  id: string;
  accountId: string;
  tagId: string;
  entityId: string;
  scope: TagScope;
  tag: AccountTag;
}

export async function listTagsForEntity(
  scope: TagScope,
  entityId: string,
): Promise<TagAssignment[]> {
  const root = scope === 'customer' ? 'customers' : scope === 'pet' ? 'pets' : 'bookings';
  const data = await request<{ assignments: TagAssignment[] }>('GET', `/${root}/${entityId}/tags`);
  return data.assignments;
}

export async function assignTag(input: {
  tagId: string;
  entityId: string;
}): Promise<TagAssignment> {
  const data = await request<{ assignment: TagAssignment }>('POST', '/tag-assignments', {
    body: input,
  });
  return data.assignment;
}

export async function unassignTag(assignmentId: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/tag-assignments/${assignmentId}`);
}

// ---------------------------------------------------------------------------
// Vets directory
// ---------------------------------------------------------------------------

export async function listVets(opts?: { all?: boolean }): Promise<Vet[]> {
  const res = await request<{ items: Vet[] }>('GET', '/vets', {
    query: opts?.all ? { all: '1' } : {},
  });
  return res.items;
}

export async function getVet(id: string): Promise<Vet> {
  const res = await request<{ vet: Vet }>('GET', `/vets/${id}`);
  return res.vet;
}

export async function createVet(input: CreateVetInput): Promise<Vet> {
  const res = await request<{ vet: Vet }>('POST', '/vets', { body: input });
  return res.vet;
}

export async function updateVet(id: string, patch: UpdateVetInput): Promise<Vet> {
  const res = await request<{ vet: Vet }>('PATCH', `/vets/${id}`, { body: patch });
  return res.vet;
}

export async function deactivateVet(id: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/vets/${id}`);
}

// ---------------------------------------------------------------------------
// Pet↔vet links
// ---------------------------------------------------------------------------

export async function listVetsForPet(petId: string): Promise<PetVetLink[]> {
  const res = await request<{ items: PetVetLink[] }>('GET', `/pets/${petId}/vets`);
  return res.items;
}

export async function linkVetToPet(
  petId: string,
  vetId: string,
  opts?: { isPrimary?: boolean; notes?: string | null },
): Promise<PetVetLink> {
  const res = await request<{ link: PetVetLink }>('POST', `/pets/${petId}/vets`, {
    body: { vetId, ...opts },
  });
  return res.link;
}

export async function unlinkVetFromPet(petId: string, vetId: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/pets/${petId}/vets/${vetId}`);
}

export async function setPrimaryVetForPet(petId: string, vetId: string): Promise<PetVetLink> {
  const res = await request<{ link: PetVetLink }>('POST', `/pets/${petId}/vets/${vetId}/primary`);
  return res.link;
}

// ---------------------------------------------------------------------------
// Pet-level belongings
// ---------------------------------------------------------------------------

export async function listPetBelongings(
  petId: string,
  opts?: { all?: boolean },
): Promise<PetBelonging[]> {
  const res = await request<{ items: PetBelonging[] }>('GET', `/pets/${petId}/belongings`, {
    query: opts?.all ? { all: '1' } : {},
  });
  return res.items;
}

export async function createPetBelonging(
  petId: string,
  input: CreatePetBelongingInput,
): Promise<PetBelonging> {
  const res = await request<{ item: PetBelonging }>('POST', `/pets/${petId}/belongings`, {
    body: input,
  });
  return res.item;
}

export async function updatePetBelonging(
  petId: string,
  bid: string,
  patch: UpdatePetBelongingInput,
): Promise<PetBelonging> {
  const res = await request<{ item: PetBelonging }>('PATCH', `/pets/${petId}/belongings/${bid}`, {
    body: patch,
  });
  return res.item;
}

export async function deactivatePetBelonging(petId: string, bid: string): Promise<void> {
  await request<{ ok: true }>('DELETE', `/pets/${petId}/belongings/${bid}`);
}

// ---------------------------------------------------------------------------
// Incidents
// ---------------------------------------------------------------------------

export async function listIncidents(
  filters: ListIncidentsFilters = {},
): Promise<{ incidents: Incident[]; nextCursor: string | null }> {
  const query: Record<string, string | number | undefined | null> = {};
  if (filters.status) query.status = filters.status;
  if (filters.severity) query.severity = filters.severity;
  if (filters.type) query.type = filters.type;
  if (filters.facilityId) query.facilityId = filters.facilityId;
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.followUpRequired) query.followUpRequired = '1';
  if (filters.cursor) query.cursor = filters.cursor;
  if (filters.limit) query.limit = filters.limit;
  return request<{ incidents: Incident[]; nextCursor: string | null }>('GET', '/incidents', {
    query,
  });
}

export interface IncidentDetail {
  incident: Incident;
  petName: string | null;
  customerId: string | null;
  customerName: string | null;
}

export async function getIncident(id: string): Promise<IncidentDetail> {
  return request<IncidentDetail>('GET', `/incidents/${id}`);
}

export async function createIncident(input: CreateIncidentInput): Promise<Incident> {
  const res = await request<{ incident: Incident }>('POST', '/incidents', { body: input });
  return res.incident;
}

export async function updateIncident(id: string, patch: UpdateIncidentInput): Promise<Incident> {
  const res = await request<{ incident: Incident }>('PATCH', `/incidents/${id}`, { body: patch });
  return res.incident;
}

export async function resolveIncident(id: string): Promise<Incident> {
  const res = await request<{ incident: Incident }>('POST', `/incidents/${id}/resolve`, {
    body: {},
  });
  return res.incident;
}

export async function notifyOwnerIncident(
  id: string,
  input: NotifyOwnerIncidentInput,
): Promise<Incident> {
  const res = await request<{ incident: Incident }>('POST', `/incidents/${id}/notify-owner`, {
    body: input,
  });
  return res.incident;
}

export async function listPetIncidents(
  petId: string,
  opts: { includeArchived?: boolean } = {},
): Promise<Incident[]> {
  const res = await request<{ incidents: Incident[] }>('GET', `/pets/${petId}/incidents`, {
    query: opts.includeArchived ? { includeArchived: '1' } : {},
  });
  return res.incidents;
}

// ---------------------------------------------------------------------------
// Vehicles & transport
// ---------------------------------------------------------------------------

export const listVehicles = (): Promise<{ vehicles: Vehicle[] }> =>
  request<{ vehicles: Vehicle[] }>('GET', '/vehicles');

// Transport fee quote for the booking wizard — POST /api/transport/quote.
// Prices from the account's transport settings; distance is the straight-line
// facility → customer distance when the pricing model needs one.
export interface TransportQuoteInput {
  facilityId: string;
  customerId?: string | null;
  /** Explicit address (staff edited it) — overrides the customer record. */
  address?: string | null;
  /** Explicit what3words square — overrides the customer record. */
  what3words?: string | null;
  country?: string | null;
  legs?: 1 | 2;
}

export type TransportLocationSource = 'what3words' | 'address';

export interface TransportQuoteEnd {
  address: string | null;
  what3words: string | null;
  point: { lat: number; lng: number } | null;
  /** How the point was resolved; null when it was not. */
  source: TransportLocationSource | null;
}

export type TransportQuoteBasis =
  | 'unpriced'
  | 'flat_fee'
  | 'per_mile'
  | 'free_radius'
  | 'distance_unknown';

export interface TransportQuote {
  configured: boolean;
  /** True when the platform has a what3words API key (squares are verified + geocoded). */
  what3wordsConfigured: boolean;
  pricingModel: 'flat_fee' | 'per_mile' | null;
  legs: number;
  feeMinor: number;
  perLegMinor: number;
  basis: TransportQuoteBasis;
  distanceMiles: number | null;
  distanceKind: 'straight_line' | null;
  origin: TransportQuoteEnd;
  destination: TransportQuoteEnd;
  driverNotes: string | null;
  issues: string[];
}

export const quoteTransport = (body: TransportQuoteInput): Promise<TransportQuote> =>
  request<TransportQuote>('POST', '/transport/quote', { body });

// what3words (migration 0104) — proxied so the browser never holds the key.
export interface What3WordsSuggestion {
  words: string;
  nearestPlace: string | null;
  country: string | null;
}

export const what3wordsAutosuggest = (input: string, country?: string | null) =>
  request<{ configured: boolean; suggestions: What3WordsSuggestion[] }>(
    'GET',
    '/transport/what3words/autosuggest',
    { query: { input, country: country ?? undefined } },
  );

export const checkWhat3Words = (what3words: string) =>
  request<{
    ok: true;
    words: string;
    verified: boolean;
    nearestPlace: string | null;
    country: string | null;
  }>('POST', '/transport/what3words/check', { body: { what3words } });

export const getVehicle = (id: string): Promise<{ vehicle: Vehicle }> =>
  request<{ vehicle: Vehicle }>('GET', `/vehicles/${id}`);

export const createVehicle = (input: CreateVehicleInput): Promise<{ vehicle: Vehicle }> =>
  request<{ vehicle: Vehicle }>('POST', '/vehicles', { body: input });

export const updateVehicle = (
  id: string,
  input: UpdateVehicleInput,
): Promise<{ vehicle: Vehicle }> =>
  request<{ vehicle: Vehicle }>('PATCH', `/vehicles/${id}`, { body: input });

export const deleteVehicle = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('DELETE', `/vehicles/${id}`);

export const listBookingTransport = (
  bookingId: string,
): Promise<{ assignments: TransportAssignment[] }> =>
  request<{ assignments: TransportAssignment[] }>('GET', `/bookings/${bookingId}/transport`);

export const addBookingTransport = (
  bookingId: string,
  input: Omit<CreateTransportInput, 'bookingId'>,
): Promise<{ assignment: TransportAssignment }> =>
  request<{ assignment: TransportAssignment }>('POST', `/bookings/${bookingId}/transport`, {
    body: input,
  });

export const getTransportManifest = (
  date: string,
  vehicleId?: string,
): Promise<{ date: string; vehicleId: string | null; assignments: TransportAssignment[] }> =>
  request<{ date: string; vehicleId: string | null; assignments: TransportAssignment[] }>(
    'GET',
    '/transport/manifest',
    { query: vehicleId ? { date, vehicleId } : { date } },
  );

export const updateTransportAssignment = (
  id: string,
  input: {
    status?: string;
    vehicleId?: string | null;
    driverMembershipId?: string | null;
    notes?: string | null;
  },
): Promise<{ assignment: TransportAssignment }> =>
  request<{ assignment: TransportAssignment }>('PATCH', `/transport/${id}`, { body: input });

// ---------- AI Copilot ----------

export interface CopilotSettings {
  id: string;
  accountId: string;
  isEnabled: number;
  dailyBriefAiSummary: number;
  customerReplyDrafts: number;
  noShowPredictions: number;
  anomalyAlerts: number;
  monthlyTokenCap: number;
  tokensUsedThisMonth: number;
  monthResetAt: string;
  brandVoiceNotes: string | null;
  aiConfigured?: boolean;
}

export interface CopilotUsageRow {
  id: string;
  feature: string;
  promptTokens: number;
  completionTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  model: string;
  latencyMs: number;
  resultStatus: string;
  createdAt: string;
}

export interface CopilotUsageResponse {
  monthlyTokenCap: number;
  tokensUsedThisMonth: number;
  monthResetAt: string;
  recent: CopilotUsageRow[];
}

export interface NoShowScoreEntry {
  score: number;
  reasons: string[];
  cached?: boolean;
}

export interface AskAiResponse {
  answer: string;
  toolCallCount: number;
  toolCalls: Array<{ name: string; input: Record<string, unknown> }>;
}

export interface CopilotAnomaliesResponse {
  date: string;
  anomalies: Array<{
    key: string;
    direction: 'up' | 'down';
    today: number;
    trailing28dMean: number;
    message: string;
  }>;
  cached: boolean;
}

export const getCopilotSettings = () => request<CopilotSettings>('GET', '/copilot/settings');
export const updateCopilotSettings = (patch: Partial<CopilotSettings>) =>
  request<CopilotSettings>('PATCH', '/copilot/settings', { body: patch });
export const getCopilotUsage = () => request<CopilotUsageResponse>('GET', '/copilot/usage');
export const getCopilotAnomalies = (date?: string) =>
  request<CopilotAnomaliesResponse>('GET', '/copilot/anomalies', date ? { query: { date } } : {});
export const draftReplyForBooking = (bookingId: string, instructions?: string) =>
  request<{ draft: string; model: string; usageBillable: number }>('POST', '/copilot/draft-reply', {
    body: { bookingId, ...(instructions ? { instructions } : {}) },
  });
export const scoreNoShowBookings = (bookingIds: string[]) =>
  request<{ scores: Record<string, NoShowScoreEntry> }>('POST', '/copilot/no-show-score', {
    body: { bookingIds },
  });
export const askAi = (question: string) =>
  request<AskAiResponse>('POST', '/copilot/ask', { body: { question } });

// ----- Comms inbox (migration 0076)
export type CommInboxFilter = 'all' | 'unread' | 'awaiting';

export interface CommInboxThread {
  id: string;
  customerId: string;
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
  lastMessageAt: string;
  lastMessageExcerpt: string;
  lastMessageSender: 'customer' | 'staff';
  lastBookingId: string | null;
  unreadCount: number;
  awaitingReplySince: string | null;
}

export interface CommInboxSnapshot {
  counts: { all: number; unread: number; awaiting: number };
  threads: CommInboxThread[];
}

export interface CommInboxThreadMessage {
  id: string;
  bookingId: string;
  sender: 'customer' | 'staff';
  body: string;
  attachmentFileIds: string[];
  isInternalNote: boolean;
  readAt: string | null;
  staffSeenAt: string | null;
  repliedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
}

export interface CommInboxThreadResponse {
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
  };
  summary: {
    unreadCount: number;
    awaitingReplySince: string | null;
    lastMessageAt: string;
  } | null;
  bookings: Array<{ id: string; type: string; status: string; createdAt: string }>;
  messages: CommInboxThreadMessage[];
}

export interface CommInboxSettings {
  awaitingReplyThresholdHours: number;
  commInboxAutoSuggest: boolean;
}

export const getCommInboxSnapshot = (filter: CommInboxFilter = 'all', search?: string) =>
  request<CommInboxSnapshot>('GET', '/comm-inbox/snapshot', {
    query: { filter, ...(search ? { search } : {}) },
  });

export const getCommInboxThread = (customerId: string) =>
  request<CommInboxThreadResponse>('GET', `/comm-inbox/threads/${customerId}`);

export const markCommInboxThreadRead = (customerId: string) =>
  request<{ markedRead: number }>('POST', `/comm-inbox/threads/${customerId}/mark-read`);

export const postCommInboxInternalNote = (customerId: string, bookingId: string, body: string) =>
  request<{ message: CommInboxThreadMessage }>(
    'POST',
    `/comm-inbox/threads/${customerId}/internal-note`,
    { body: { bookingId, body } },
  );

export const markCommInboxAllRead = () =>
  request<{ threadsTouched: number; messagesMarked: number }>('POST', '/comm-inbox/mark-all-read');

export const getCommInboxSettings = () => request<CommInboxSettings>('GET', '/comm-inbox/settings');

export const updateCommInboxSettings = (patch: Partial<CommInboxSettings>) =>
  request<{ ok: boolean }>('PATCH', '/comm-inbox/settings', { body: patch });

export const sendBookingMessage = (bookingId: string, body: string) =>
  request<{ message: { id: string; createdAt: string; sender: string; body: string } }>(
    'POST',
    `/bookings/${bookingId}/messages`,
    { body: { body } },
  );

// ----- Customer 360 (Customer 360 overhaul)
export interface Customer360Response {
  customer: Record<string, unknown> & { id: string; firstName: string; lastName: string };
  pets: Array<Record<string, unknown>>;
  bookings: Array<{ booking: Record<string, unknown>; stay: Record<string, unknown> | null }>;
  bookingsTotal: number;
  photos: Array<Record<string, unknown>>;
  payments: Array<Record<string, unknown>>;
  paymentsTotalsMinor: { spend: number; refund: number; net: number };
  loyalty: { balance: number; enabled: boolean; recent: Array<Record<string, unknown>> };
  referral: { code: string | null; isActive: boolean; redemptions: number };
  credits: Array<Record<string, unknown>>;
  packs: Array<Record<string, unknown>>;
  memberships: Array<Record<string, unknown>>;
  vaccinations: Array<Record<string, unknown>>;
  notes: Array<Record<string, unknown>>;
  messages: Array<Record<string, unknown>>;
  tags: Array<Record<string, unknown>>;
  notificationPreferences: Array<Record<string, unknown>>;
  activityTimeline: Array<Record<string, unknown>>;
  stats: {
    totalBookings: number;
    completedBookings: number;
    cancelledBookings: number;
    noShowBookings: number;
    cancellationRatePercent: number;
    noShowRatePercent: number;
    avgBookingValueMinor: number;
    activeCreditsMinor: number;
    activePacks: number;
    referralsMade: number;
  };
  lifecycleStage: 'new' | 'regular' | 'vip' | 'lapsed' | 'churned';
  lifecycleThresholds: Record<string, number>;
  churnScore: {
    score: number;
    signals: string[];
    suggestedAction: string;
    cachedAt: string;
  } | null;
}

export const getCustomer360 = (id: string) =>
  request<Customer360Response>('GET', `/customers/${id}/360`);

export interface ChurnScoreResult {
  customerId: string;
  score: number;
  signals: string[];
  suggestedAction: string;
  cached: boolean;
}

export const computeCustomerChurnScore = (customerId: string) =>
  request<ChurnScoreResult>('POST', '/copilot/customer-churn-score', { body: { customerId } });

// ── Onboarding (migration 0062) ─────────────────────────────────────────────

export type OnboardingBusinessType = 'kennel' | 'daycare' | 'grooming' | 'multi';

export interface OnboardingApplyTemplateResult {
  businessType: OnboardingBusinessType;
  created: string[];
  skipped: string[];
}

export interface OnboardingSampleDataResult {
  customers: number;
  pets: number;
  bookings: number;
  groomingAppointments: number;
}

export interface OnboardingCheckSlugResult {
  slug: string;
  available: boolean;
}

export const onboardingCheckSlug = (slug: string) =>
  request<OnboardingCheckSlugResult>('GET', '/onboarding/check-slug', { query: { slug } });

export const onboardingApplyTemplate = (businessType: OnboardingBusinessType) =>
  request<OnboardingApplyTemplateResult>('POST', '/onboarding/apply-template', {
    body: { businessType },
  });

export const onboardingSeedSampleData = () =>
  request<OnboardingSampleDataResult>('POST', '/onboarding/seed-sample-data');

export const onboardingWipeSampleData = () =>
  request<{
    customersDeleted: number;
    petsDeleted: number;
    bookingsDeleted: number;
    groomingDeleted: number;
  }>('DELETE', '/onboarding/sample-data');

export const onboardingPatchStep = (input: {
  businessType?: OnboardingBusinessType | null;
  brandVoiceNotes?: string | null;
}) => request<{ updated: string[] }>('PATCH', '/onboarding/step', { body: input });

export const onboardingComplete = () =>
  request<{ onboardingCompletedAt: string }>('POST', '/onboarding/complete');

// ---------------------------------------------------------------------------
// Pet vaccinations (staff CRUD)
// ---------------------------------------------------------------------------

export interface PetVaccination {
  id: string;
  accountId: string;
  petId: string;
  vaccineId: string;
  administeredOn: string | null;
  expiresOn: string | null;
  notes: string | null;
  verifiedByMembershipId: string | null;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePetVaccinationInput {
  vaccineId: string;
  administeredOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
}

export interface UpdatePetVaccinationInput {
  administeredOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
}

export const listPetVaccinations = (petId: string) =>
  request<{ items: PetVaccination[] }>('GET', `/pets/${petId}/vaccinations`);

export const createPetVaccination = (petId: string, body: CreatePetVaccinationInput) =>
  request<{ vaccination: PetVaccination }>('POST', `/pets/${petId}/vaccinations`, { body });

export const updatePetVaccination = (petId: string, id: string, body: UpdatePetVaccinationInput) =>
  request<{ vaccination: PetVaccination }>('PATCH', `/pets/${petId}/vaccinations/${id}`, { body });

export const deletePetVaccination = (petId: string, id: string) =>
  request<{ ok: true }>('DELETE', `/pets/${petId}/vaccinations/${id}`);

// ---------------------------------------------------------------------------
// Pet observations (health monitoring)
// ---------------------------------------------------------------------------

export interface PetObservation {
  id: string;
  accountId: string;
  petId: string;
  bookingId: string | null;
  traitsJson: string;
  notes: string | null;
  observedByMembershipId: string | null;
  createdAt: string;
}

export interface CreatePetObservationInput {
  traits: string[];
  notes?: string | null;
  bookingId?: string | null;
}

export const listPetObservations = (petId: string) =>
  request<{ items: PetObservation[] }>('GET', `/pets/${petId}/observations`);

export const createPetObservation = (petId: string, body: CreatePetObservationInput) =>
  request<{ item: PetObservation }>('POST', `/pets/${petId}/observations`, { body });

export const listBookingObservations = (bookingId: string) =>
  request<{ items: PetObservation[] }>('GET', `/bookings/${bookingId}/observations`);

export const createBookingObservation = (bookingId: string, body: CreatePetObservationInput) =>
  request<{ item: PetObservation }>('POST', `/bookings/${bookingId}/observations`, { body });

// ---------------------------------------------------------------------------
// Facilities (staff CRUD)
// ---------------------------------------------------------------------------

export interface CreateFacilityInput {
  slug: string;
  name: string;
  timezone?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
}

export interface UpdateFacilityInput {
  name?: string;
  timezone?: string | null;
  what3words?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  region?: string | null;
  postcode?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  isActive?: 0 | 1;
}

export const listFacilities = () =>
  request<{ facilities: import('./types').Facility[] }>('GET', '/facilities');

export const createFacility = (input: CreateFacilityInput) =>
  request<{ facility: import('./types').Facility }>('POST', '/facilities', { body: input });

export const updateFacility = (id: string, patch: UpdateFacilityInput) =>
  request<{ facility: import('./types').Facility }>('PATCH', `/facilities/${id}`, { body: patch });

// ----- Data import (onboarding)
export interface ImportRowError {
  row: number;
  reason: string;
}
export interface ImportRowResult {
  row: number;
  status: 'created' | 'skipped' | 'error';
  reason?: string;
  summary?: string;
  warnings?: string[];
}
export interface ImportSummary {
  created: number;
  skipped: number;
  errors: ImportRowError[];
  /** True when the request ran with `dryRun: true` — nothing was written. */
  dryRun?: boolean;
  rowCount?: number;
  /** Per-row results (first 200). Counts above always cover every row. */
  rows?: ImportRowResult[];
  /** Run-level notes, e.g. "vaccine catalogue is empty". */
  warnings?: string[];
}
/** Each row is a map of canonical-field -> string value. */
export type ImportRow = Record<string, string>;

export type ImportKind = 'customers' | 'pets' | 'vaccinations' | 'bookings';
export type ImportPreset = 'kennelbooker' | 'gingr' | 'petexec' | 'generic';
export type ImportDateFormat = 'dmy' | 'mdy' | 'iso';
export type ImportMappingConfidence = 'high' | 'medium' | 'low' | 'none';

export interface ImportPresetInfo {
  id: ImportPreset;
  label: string;
  description: string;
}
export interface ImportFieldDef {
  key: string;
  label: string;
  kinds: ImportKind[];
  requiredIn?: ImportKind[];
  hint?: string;
}
export interface ImportColumnMapping {
  index: number;
  header: string;
  normalised: string;
  field: string | null;
  confidence: ImportMappingConfidence;
  warning?: string;
}
export interface ImportHeaderMapping {
  kind: ImportKind;
  preset: ImportPreset;
  columns: ImportColumnMapping[];
  unmapped: number[];
  missingRequired: string[];
  fields: ImportFieldDef[];
}
export interface ImportPresetsResponse {
  presets: ImportPresetInfo[];
  kinds: ImportKind[];
  dateFormats: ImportDateFormat[];
  fields: Record<ImportKind, ImportFieldDef[]>;
}
export interface ImportRunOptions {
  dryRun?: boolean;
  dateFormat?: ImportDateFormat;
  preset?: ImportPreset;
  /** Bookings only — required when more than one facility is in scope. */
  facilityId?: string | null;
}
export interface ImportJob {
  id: string;
  kind: string;
  createdAt: string;
  created: number;
  skipped: number;
  errorCount: number;
  rowCount: number | null;
  preset: string | null;
  errors: ImportRowError[];
}

export const getImportPresets = () => request<ImportPresetsResponse>('GET', '/import/presets');

export const mapImportHeaders = (body: {
  headers: string[];
  kind: ImportKind;
  preset?: ImportPreset;
}) => request<ImportHeaderMapping>('POST', '/import/map-headers', { body });

export const listImportJobs = () => request<{ jobs: ImportJob[] }>('GET', '/import/jobs');

export const importCustomers = (rows: ImportRow[], opts: ImportRunOptions = {}) =>
  request<ImportSummary>('POST', '/import/customers', { body: { rows, ...opts } });

export const importPets = (rows: ImportRow[], opts: ImportRunOptions = {}) =>
  request<ImportSummary>('POST', '/import/pets', { body: { rows, ...opts } });

export const importVaccinations = (rows: ImportRow[], opts: ImportRunOptions = {}) =>
  request<ImportSummary>('POST', '/import/vaccinations', { body: { rows, ...opts } });

export const importBookings = (rows: ImportRow[], opts: ImportRunOptions = {}) =>
  request<ImportSummary>('POST', '/import/bookings', { body: { rows, ...opts } });

/** Dispatch by kind so the settings page needs one call site. */
export const runImport = (kind: ImportKind, rows: ImportRow[], opts: ImportRunOptions = {}) => {
  switch (kind) {
    case 'customers':
      return importCustomers(rows, opts);
    case 'pets':
      return importPets(rows, opts);
    case 'vaccinations':
      return importVaccinations(rows, opts);
    case 'bookings':
      return importBookings(rows, opts);
  }
};

// ----- Staff rota (migration 0085) -----
import type {
  CreateStaffShiftInput,
  CreateStaffShiftTemplateInput,
  StaffShift,
  StaffShiftTemplate,
  UpdateStaffShiftInput,
  UpdateStaffShiftTemplateInput,
} from './types';

export const listStaffShifts = (
  facilityId: string,
  from: string,
  to: string,
): Promise<StaffShift[]> =>
  request<StaffShift[]>(
    'GET',
    `/staff-shifts?facilityId=${encodeURIComponent(facilityId)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );

export const createStaffShift = (body: CreateStaffShiftInput): Promise<StaffShift> =>
  request<StaffShift>('POST', '/staff-shifts', { body });

export const updateStaffShift = (id: string, patch: UpdateStaffShiftInput): Promise<StaffShift> =>
  request<StaffShift>('PATCH', `/staff-shifts/${id}`, { body: patch });

export const deleteStaffShift = (id: string): Promise<void> =>
  request<void>('DELETE', `/staff-shifts/${id}`);

export const listStaffShiftTemplates = (): Promise<StaffShiftTemplate[]> =>
  request<StaffShiftTemplate[]>('GET', '/staff-shift-templates');

export const createStaffShiftTemplate = (
  body: CreateStaffShiftTemplateInput,
): Promise<StaffShiftTemplate> =>
  request<StaffShiftTemplate>('POST', '/staff-shift-templates', { body });

export const updateStaffShiftTemplate = (
  id: string,
  patch: UpdateStaffShiftTemplateInput,
): Promise<StaffShiftTemplate> =>
  request<StaffShiftTemplate>('PATCH', `/staff-shift-templates/${id}`, { body: patch });

export const deleteStaffShiftTemplate = (id: string): Promise<void> =>
  request<void>('DELETE', `/staff-shift-templates/${id}`);

// ----- Household co-owners (migration 0102)
export interface StaffHouseholdMember {
  id: string;
  role: 'primary' | 'member';
  status: 'active' | 'pending';
  name: string | null;
  email: string | null;
  permissions: {
    canBook: boolean;
    canPay: boolean;
    canEditPets: boolean;
    receivesNotifications: boolean;
  };
  invitedAt: string | null;
  acceptedAt: string | null;
}

export interface StaffHouseholdResponse {
  customerId: string;
  hasPrimary: boolean;
  members: StaffHouseholdMember[];
}

/** Read-only household for the customer detail page. */
export function getCustomerHousehold(customerId: string): Promise<StaffHouseholdResponse> {
  return request<StaffHouseholdResponse>('GET', `/customers/${customerId}/household`);
}

/** Invite a co-owner on the customer's behalf (customers.edit). Fires customer.household_invite. */
export function inviteHouseholdMember(
  customerId: string,
  email: string,
): Promise<{ id: string; invitedEmail: string; url: string; expiresAt: string; reused: boolean }> {
  return request('POST', `/customers/${customerId}/household/invites`, { body: { email } });
}
// ---------------------------------------------------------------------------
// Licensing records (migration 0100 — UK licensing inspection pack)
// ---------------------------------------------------------------------------

export interface FacilityLicence {
  id: string;
  accountId: string;
  facilityId: string;
  authorityName: string;
  licenceNumber: string | null;
  activityTypesJson: string;
  activityTypes: string[];
  starRating: number | null;
  issuedOn: string | null;
  expiresOn: string | null;
  lastInspectionOn: string | null;
  nextInspectionDueOn: string | null;
  notes: string | null;
  documentFileId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Only present on the account-wide listing. */
  facilityName?: string | null;
}

export interface FacilityLicenceInput {
  authorityName?: string;
  licenceNumber?: string | null;
  activityTypes?: string[];
  starRating?: number | null;
  issuedOn?: string | null;
  expiresOn?: string | null;
  lastInspectionOn?: string | null;
  nextInspectionDueOn?: string | null;
  notes?: string | null;
}

export const listAllLicences = () =>
  request<{ items: FacilityLicence[] }>('GET', '/compliance/licences');
export const listFacilityLicences = (facilityId: string) =>
  request<{ items: FacilityLicence[] }>('GET', `/facilities/${facilityId}/licences`);
export const createFacilityLicence = (facilityId: string, body: FacilityLicenceInput) =>
  request<{ licence: FacilityLicence }>('POST', `/facilities/${facilityId}/licences`, { body });
export const updateFacilityLicence = (
  facilityId: string,
  licenceId: string,
  body: FacilityLicenceInput,
) =>
  request<{ licence: FacilityLicence }>(
    'PATCH',
    `/facilities/${facilityId}/licences/${licenceId}`,
    { body },
  );
export const deleteFacilityLicence = (facilityId: string, licenceId: string) =>
  request<void>('DELETE', `/facilities/${facilityId}/licences/${licenceId}`);

export interface CleaningLogEntry {
  id: string;
  accountId: string;
  facilityId: string;
  runId: string | null;
  areaLabel: string;
  performedAt: string;
  performedByMembershipId: string | null;
  productUsed: string | null;
  notes: string | null;
  createdAt: string;
}

export interface CreateCleaningLogInput {
  facilityId: string;
  runId?: string | null;
  areaLabel?: string;
  productUsed?: string | null;
  notes?: string | null;
  performedAt?: string;
}

export const createCleaningLogEntry = (body: CreateCleaningLogInput) =>
  request<{ entry: CleaningLogEntry }>('POST', '/cleaning-log', { body });
export const listCleaningLog = (
  facilityId: string,
  opts: { runId?: string; from?: string; to?: string; limit?: number } = {},
) =>
  request<{ items: CleaningLogEntry[] }>('GET', '/cleaning-log', {
    query: { facilityId, ...opts },
  });

export type DailyMonitoringAppetite = 'normal' | 'reduced' | 'none';
export type DailyMonitoringToileting = 'normal' | 'loose' | 'none' | 'other';
export type DailyMonitoringDemeanour = 'settled' | 'anxious' | 'lethargic' | 'other';

export interface DailyMonitoringEntry {
  id: string;
  accountId: string;
  bookingId: string;
  petId: string;
  observedAt: string;
  observedByMembershipId: string | null;
  appetite: DailyMonitoringAppetite;
  toileting: DailyMonitoringToileting;
  demeanour: DailyMonitoringDemeanour;
  notes: string | null;
  createdAt: string;
}

export interface CreateDailyMonitoringInput {
  appetite: DailyMonitoringAppetite;
  toileting: DailyMonitoringToileting;
  demeanour: DailyMonitoringDemeanour;
  notes?: string | null;
  observedAt?: string;
}

export const createMonitoringEntry = (bookingId: string, body: CreateDailyMonitoringInput) =>
  request<{ entry: DailyMonitoringEntry }>('POST', `/bookings/${bookingId}/monitoring`, { body });
export const listMonitoringEntries = (bookingId: string) =>
  request<{ items: DailyMonitoringEntry[] }>('GET', `/bookings/${bookingId}/monitoring`);

export interface PetRegister {
  pet: {
    id: string;
    name: string;
    species: string;
    breed: string | null;
    colour: string | null;
    dateOfBirth: string | null;
    sex: string | null;
    neutered: number | null;
    microchip: string | null;
    microchipRegisteredWith: string | null;
  };
  owner: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    address: string | null;
  };
  emergencyContact: { name: string | null; phone: string | null; relationship: string | null };
  localEmergencyContact: { name: string | null; phone: string | null };
  vet: {
    source: 'registered' | 'pet_record' | null;
    practiceName: string | null;
    vetName: string | null;
    phone: string | null;
    email: string | null;
    emergencyPhone: string | null;
  };
  insurance: { provider: string | null; policyNumber: string | null; expiresOn: string | null };
  medical: {
    conditions: string | null;
    allergies: string | null;
    medications: string | null;
    medicationSchedule: string | null;
    behaviour: string | null;
    specialInstructions: string | null;
  };
  diet: { dietNotes: string | null; feedingInstructions: string | null };
  parasiteTreatment: { wormingLastOn: string | null; fleaLastOn: string | null };
  consents: {
    vetConsentGivenAt: string | null;
    waiverAcceptedAt: string | null;
    waiverVersion: number | null;
  };
  vaccinations: Array<{
    vaccineName: string;
    administeredOn: string | null;
    expiresOn: string | null;
    verifiedAt: string | null;
  }>;
  stays: Array<{
    bookingId: string;
    status: string;
    arrival: string;
    departure: string;
    actualDeparture: string | null;
    runId: string | null;
    runName: string | null;
    feeding: string | null;
    medication: string | null;
  }>;
  missing: string[];
}

export const getPetRegister = (petId: string) =>
  request<{ register: PetRegister }>('GET', `/pets/${petId}/register`);

export interface InspectionPackResult {
  fileId: string;
  filename: string;
  downloadUrl: string;
  expiresAt: string;
  archiveSize: number;
  counts: {
    animals: number;
    stays: number;
    medicationEntries: number;
    monitoringEntries: number;
    cleaningEntries: number;
    incidents: number;
    vaccinations: number;
    licences: number;
  };
}

export const createInspectionPack = (body: { facilityId: string; from: string; to: string }) =>
  request<InspectionPackResult>('POST', '/compliance/inspection-pack', { body });
